import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Col, Dropdown, Form, Modal, Nav, OverlayTrigger, Row, Spinner, Table, Tab, Tabs, Tooltip } from "react-bootstrap";
import { FaAngleLeft, FaAngleRight, FaArchive, FaBell, FaBook, FaClipboardList, FaClock, FaCog, FaComments, FaCopy, FaEdit, FaExclamationTriangle, FaExternalLinkAlt, FaEyeSlash, FaFileAlt, FaFileDownload, FaFolder, FaHistory, FaLink, FaPlay, FaQuestionCircle, FaThumbtack, FaTimes, FaUndo, FaUsers, FaUserMinus } from "react-icons/fa";
import {
  OobArtifactLibraryItemDto,
  OobBulkMode,
  OobCreateEventRequest,
  OobCreateParticipantRequest,
  OobEmployeeLookupDto,
  OobCreateUpdateRequest,
  OobEditUpdateRequest,
  OobEventArtifactDto,
  OobEventArtifactManifestDto,
  OobEventDto,
  OobEventQuestionDto,
  OobEditEventQuestionRequest,
  OobLocalEventChannelDto,
  OobLocalEventMessageDto,
  OobEventParticipantDto,
  OobAuditEventDto,
  OobAdminEmergencyProfileDto,
  OobParticipantGroupDto,
  OobParticipantGroupMemberDto,
  OobParticipantDto,
  OobReadinessDashboardDto,
  OobSecondaryFollowUpDto,
  OobSecondaryProvisioningStatusDto,
  OobStatusUpdateDto,
  activateOobLocalEventSpace,
  activateOobEvent,
  addOobEventParticipant,
  archiveOobUpdate,
  bulkImportOobParticipants,
  closeOobEvent,
  createOobEvent,
  createOobParticipantGroup,
  createOobParticipant,
  deprovisionOobEvent,
  downloadOobAdminEventArtifact,
  downloadOobLibraryArtifact,
  editOobUpdate,
  editOobAdminEventQuestion,
  finalizeOobEvent,
  getOobArtifactLibrary,
  getAdminOobPlatformAnnouncements,
  getMyOobAdminEmergencyProfile,
  getOobAcknowledgementSummary,
  getOobAudit,
  getOobEvent,
  getOobAdminEventQuestions,
  getOobEventArtifacts,
  getOobEventFollowUps,
  getOobEventParticipants,
  getOobEvents,
  getOobLatestEventArtifactManifest,
  getOobAdminLocalEventChannelMessages,
  getOobAdminLocalEventChannels,
  getOobParticipantGroupMembers,
  getOobParticipantGroups,
  getOobParticipants,
  searchOobEmployeeDirectory,
  getOobReadinessDashboard,
  getOobSecondaryProvisioningStatus,
  archiveOobArtifactFromLibrary,
  pinOobUpdate,
  publishOobAdminEventQuestion,
  postOobUpdate,
  postOobAdminLocalEventChannelMessage,
  purgeOobAll,
  purgeOobGroup,
  queueOobPlatformAnnouncement,
  reconcileOobEventMembership,
  removeOobEventParticipant,
  removeOobParticipantFromGroup,
  renewOobBreakGlassSession,
  resendOobPending,
  resolveOobEvent,
  resolveOobEventFollowUp,
  retryOobSecondaryProvisioning,
  retractOobUpdate,
  unretractOobUpdate,
  updateMyOobAdminEmergencyProfile,
  updateOobParticipantGroups,
  updateOobPivotEvent,
  uploadOobArtifactToLibrary,
  inviteOobSecondaryIdentity,
} from "@/api/outOfBandApi";
import { OobDeliveryStatusBanner, OobEventMetricStrip, OobEventWorkspaceHeader } from "@/components/Oob/OobEventWorkspace";

const defaultParticipant: OobCreateParticipantRequest = {
  employeeId: "",
  email: "",
  displayName: "",
  department: "",
  location: "",
  region: "",
  phoneNumber: "",
  isDefaultParticipant: true,
  groupGuids: [],
};

const defaultEvent: OobCreateEventRequest = {
  eventName: "",
  eventType: "Test",
  safetyPhrase: "",
  description: "",
  createLocalEvent: false,
  groupGuids: [],
  isSilent: false,
};

const defaultUpdate: OobCreateUpdateRequest = {
  messageTitle: "",
  messageBody: "",
  importance: "Normal",
  isPinned: false,
};

const fmt = (d?: string) => (d ? new Date(d).toLocaleString() : "-");
const fmtBytes = (bytes?: number) => {
  if (bytes == null || Number.isNaN(bytes)) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

const artifactCategoryStyles: Record<string, { variant: string; icon: React.ComponentType }> = {
  RunBook: { variant: "primary", icon: FaBook },
  EmergencyPlan: { variant: "danger", icon: FaExclamationTriangle },
  Checklist: { variant: "success", icon: FaClipboardList },
  Other: { variant: "secondary", icon: FaFileAlt },
};

const ArtifactCategoryBadge: React.FC<{ category: string }> = ({ category }) => {
  const style = artifactCategoryStyles[category] || artifactCategoryStyles.Other;
  const Icon = style.icon;
  return (
    <Badge bg={style.variant} className="d-inline-flex align-items-center gap-1">
      <Icon />
      {category}
    </Badge>
  );
};

const eventStatusBadgeVariants: Record<string, string> = {
  Draft: "secondary",
  Active: "danger",
  Resolved: "warning",
  Finalized: "primary",
  Deprovisioned: "dark",
  Closed: "secondary",
};

const getEventStatusBadgeVariant = (status: string) => eventStatusBadgeVariants[status] || "secondary";

const fmtRemaining = (ms: number) => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }

  return `${minutes}m`;
};

const formatEventDuration = (startedAt?: string) => {
  if (!startedAt) return "-";
  const normalizedStartedAt = /(?:Z|[+-]\d{2}:\d{2})$/i.test(startedAt) ? startedAt : `${startedAt}Z`;
  const elapsedMinutes = Math.max(0, Math.floor((Date.now() - new Date(normalizedStartedAt).getTime()) / 60000));
  if (elapsedMinutes < 1) return "Just started";
  const days = Math.floor(elapsedMinutes / (60 * 24));
  const hours = Math.floor((elapsedMinutes % (60 * 24)) / 60);
  const minutes = elapsedMinutes % 60;
  if (days > 0) return `${days}d ${hours}h`;
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
};

const publicEventPath = (eventGuid: string) => `/admin/oob/${eventGuid}`;
const participantPublicPath = (eventGuid: string) => `/oob/${eventGuid}`;

const getUpdateLifecycle = (update: OobStatusUpdateDto): "Published" | "Retracted" | "Archived" => {
  if (update.effEndDt) {
    return "Archived";
  }

  if (update.retractedDt) {
    return "Retracted";
  }

  return "Published";
};

const getApiErrorMessage = (err: any, fallback: string) => {
  const data = err?.response?.data;
  if (data?.error) return data.error;
  if (data?.message) return data.message;
  if (data?.title) return data.title;
  if (data?.errors && typeof data.errors === "object") {
    const first = Object.values(data.errors)
      .flatMap((v: any) => (Array.isArray(v) ? v : [String(v)]))
      .find((msg: any) => typeof msg === "string" && msg.trim().length > 0);
    if (first) return first as string;
  }
  return err?.message || fallback;
};

type PlatformAnnouncementForm = {
  title: string;
  message: string;
};

type PlatformAnnouncementRow = {
  announcementGuid: string;
  title: string;
  message: string;
  createdBy: string;
  recipientCount: number;
  queuedCount: number;
  createDt: string;
};

type ConfirmAction =
  | { type: "retractUpdate"; updateGuid: string }
  | { type: "unretractUpdate"; updateGuid: string }
  | { type: "archiveUpdate"; updateGuid: string }
  | { type: "removeEventParticipant"; participantGuid: string };

type UpdateLifecycleFilter = "Active" | "Published" | "Retracted" | "Archived" | "All";
type AuditSortColumn = "time" | "type" | "severity" | "user";

type EditUpdateState = {
  updateGuid: string;
  messageTitle: string;
  messageBody: string;
  importance: "Normal" | "Important" | "Critical";
};

type EditEventQuestionState = OobEditEventQuestionRequest & { questionGuid: string };

const AdminOutOfBandBoard: React.FC = () => {
  const [tab, setTab] = useState<string>("home");
  const [eventSubTab, setEventSubTab] = useState<string>("updates");
  const [eventNavigationCollapsed, setEventNavigationCollapsed] = useState(false);
  const [eventSidebarCollapsed, setEventSidebarCollapsed] = useState(false);
  const [autoFocusedActiveEvent, setAutoFocusedActiveEvent] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [adminSessionRemainingMs, setAdminSessionRemainingMs] = useState<number | null>(null);
  const [adminSessionWarning, setAdminSessionWarning] = useState<string | null>(null);
  const [renewingAdminSession, setRenewingAdminSession] = useState(false);
  const [showAdminGettingStarted, setShowAdminGettingStarted] = useState(false);
  const [adminEmergencyProfile, setAdminEmergencyProfile] = useState<OobAdminEmergencyProfileDto | null>(null);
  const [showAdminEmergencyModal, setShowAdminEmergencyModal] = useState(false);
  const [adminEmergencyEmail, setAdminEmergencyEmail] = useState("");
  const [adminEmergencyPhone, setAdminEmergencyPhone] = useState("");

  const [participants, setParticipants] = useState<OobParticipantDto[]>([]);
  const [participantForm, setParticipantForm] = useState<OobCreateParticipantRequest>(defaultParticipant);

  const [events, setEvents] = useState<OobEventDto[]>([]);
  const [selectedEventGuid, setSelectedEventGuid] = useState<string>("");
  const [selectedEvent, setSelectedEvent] = useState<OobEventDto | null>(null);
  const [secondaryProvisioningStatus, setSecondaryProvisioningStatus] = useState<OobSecondaryProvisioningStatusDto | null>(null);
  const [secondaryProvisioningLoading, setSecondaryProvisioningLoading] = useState(false);
  const [eventFollowUps, setEventFollowUps] = useState<OobSecondaryFollowUpDto[]>([]);
  const [reconcileCheckedClean, setReconcileCheckedClean] = useState(false);
  const [lifecycleActionLoading, setLifecycleActionLoading] = useState(false);
  const [followUpNotes, setFollowUpNotes] = useState<Record<string, string>>({});
  const [resolvingFollowUpGuid, setResolvingFollowUpGuid] = useState<string | null>(null);
  const [eventForm, setEventForm] = useState<OobCreateEventRequest>(defaultEvent);
  const [showCreateEventWizard, setShowCreateEventWizard] = useState(false);
  const [createEventStep, setCreateEventStep] = useState<1 | 2>(1);
  const [eventTypeFilter, setEventTypeFilter] = useState<string>("All");
  const [eventStatusFilter, setEventStatusFilter] = useState<string>("All");
  const [updateLifecycleFilter, setUpdateLifecycleFilter] = useState<UpdateLifecycleFilter>("Active");
  const [showActionableOnly, setShowActionableOnly] = useState(false);
  const [updateForm, setUpdateForm] = useState<OobCreateUpdateRequest>(defaultUpdate);
  const [showPostUpdateModal, setShowPostUpdateModal] = useState(false);
  const [editUpdateState, setEditUpdateState] = useState<EditUpdateState | null>(null);

  const [bulkMode, setBulkMode] = useState<OobBulkMode>("AddNew");
  const [bulkFile, setBulkFile] = useState<File | null>(null);
  const [selectedBulkGroupGuid, setSelectedBulkGroupGuid] = useState<string>("");

  const [acks, setAcks] = useState<any[]>([]);
  const [audit, setAudit] = useState<OobAuditEventDto[]>([]);
  const [auditSearch, setAuditSearch] = useState("");
  const [auditSeverityFilter, setAuditSeverityFilter] = useState<string>("All");
  const [auditTypeFilter, setAuditTypeFilter] = useState<string>("All");
  const [auditUserFilter, setAuditUserFilter] = useState<string>("All");
  const [auditTimeFilter, setAuditTimeFilter] = useState<string>("All");
  const [auditIssuesOnly, setAuditIssuesOnly] = useState(false);
  const [auditSortColumn, setAuditSortColumn] = useState<AuditSortColumn>("time");
  const [auditSortAscending, setAuditSortAscending] = useState(true);
  const [eventParticipants, setEventParticipants] = useState<OobEventParticipantDto[]>([]);
  const [selectedParticipantToAdd, setSelectedParticipantToAdd] = useState<string>("");
  const [showAddEventParticipantModal, setShowAddEventParticipantModal] = useState(false);

  const [groups, setGroups] = useState<OobParticipantGroupDto[]>([]);
  const [groupMembersByGroup, setGroupMembersByGroup] = useState<Record<string, OobParticipantGroupMemberDto[]>>({});
  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);
  const [expandedParticipantGroups, setExpandedParticipantGroups] = useState<string[]>([]);
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [groupForm, setGroupForm] = useState({ groupName: "", sortOrder: 0, description: "" });
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [addMemberMode, setAddMemberMode] = useState<"existing" | "new">("existing");
  const [targetGroupForNewMember, setTargetGroupForNewMember] = useState<OobParticipantGroupDto | null>(null);
  const [existingParticipantSearch, setExistingParticipantSearch] = useState("");
  const [selectedExistingParticipantGuid, setSelectedExistingParticipantGuid] = useState("");
  const [isExistingParticipantMenuOpen, setIsExistingParticipantMenuOpen] = useState(false);
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [employeeSearchResults, setEmployeeSearchResults] = useState<OobEmployeeLookupDto[]>([]);
  const [employeeSearchLoading, setEmployeeSearchLoading] = useState(false);
  const [selectedDirectoryEmployee, setSelectedDirectoryEmployee] = useState<OobEmployeeLookupDto | null>(null);
  const [selectedAnnouncementGroups, setSelectedAnnouncementGroups] = useState<string[]>([]);
  const [includedAnnouncementParticipants, setIncludedAnnouncementParticipants] = useState<string[]>([]);
  const [excludedAnnouncementParticipants, setExcludedAnnouncementParticipants] = useState<string[]>([]);
  const [announcementForm, setAnnouncementForm] = useState<PlatformAnnouncementForm>({ title: "", message: "" });
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [announcementRows, setAnnouncementRows] = useState<PlatformAnnouncementRow[]>([]);
  const [announcementSearch, setAnnouncementSearch] = useState<string>("");
  const [readinessDashboard, setReadinessDashboard] = useState<OobReadinessDashboardDto | null>(null);
  const [readinessSearch, setReadinessSearch] = useState("");
  const [readinessGroupFilter, setReadinessGroupFilter] = useState<string>("All");
  const [readinessStateFilter, setReadinessStateFilter] = useState<string>("All");
  const [invitingParticipantGuid, setInvitingParticipantGuid] = useState<string | null>(null);
  const [activatingEventGuid, setActivatingEventGuid] = useState<string | null>(null);
  const [showLocalPivotModal, setShowLocalPivotModal] = useState(false);
  const [pivotEventForm, setPivotEventForm] = useState<OobCreateEventRequest>(defaultEvent);
  const [savingPivotReview, setSavingPivotReview] = useState(false);
  const [readinessVerificationFilter, setReadinessVerificationFilter] = useState<string>("All");
  const [readinessPreferredFilter, setReadinessPreferredFilter] = useState<string>("All");
  const [readinessAddedWindowFilter, setReadinessAddedWindowFilter] = useState<string>("All");
  const [readinessNeedsActionOnly, setReadinessNeedsActionOnly] = useState(false);
  const [readinessInviteAgeFilter, setReadinessInviteAgeFilter] = useState<string>("All");
  const [readinessActivityFilter, setReadinessActivityFilter] = useState<string>("All");
  const [readinessGroupCountFilter, setReadinessGroupCountFilter] = useState<string>("All");
  const [artifactLibrary, setArtifactLibrary] = useState<OobArtifactLibraryItemDto[]>([]);
  const [artifactUploadFile, setArtifactUploadFile] = useState<File | null>(null);
  const [artifactUploadCategory, setArtifactUploadCategory] = useState<string>("RunBook");
  const [artifactFileInputKey, setArtifactFileInputKey] = useState(0);
  const [filesSubTab, setFilesSubTab] = useState<string>("masterGallery");
  const [filesEventGuid, setFilesEventGuid] = useState<string>("");
  const [eventArtifacts, setEventArtifacts] = useState<OobEventArtifactDto[]>([]);
  const [eventFileFolder, setEventFileFolder] = useState<string>("All Files");
  const [eventQuestions, setEventQuestions] = useState<OobEventQuestionDto[]>([]);
    const [localEventChannels, setLocalEventChannels] = useState<OobLocalEventChannelDto[]>([]);
    const [selectedLocalChannelGuid, setSelectedLocalChannelGuid] = useState<string>("");
    const [localChannelMessages, setLocalChannelMessages] = useState<OobLocalEventMessageDto[]>([]);
    const [localChannelMessageBody, setLocalChannelMessageBody] = useState("");
    const [postingLocalChannelMessage, setPostingLocalChannelMessage] = useState(false);
  const [questionAnswer, setQuestionAnswer] = useState<Record<string, string>>({});
  const [publishingQuestionGuid, setPublishingQuestionGuid] = useState<string | null>(null);
  const [editEventQuestion, setEditEventQuestion] = useState<EditEventQuestionState | null>(null);
  const [eventArtifactManifest, setEventArtifactManifest] = useState<OobEventArtifactManifestDto | null>(null);

  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);
  const [showPurgeAllModal, setShowPurgeAllModal] = useState(false);
  const [purgeAllOptions, setPurgeAllOptions] = useState({ purgeParticipants: true, purgeGroups: false });
  const [showPurgeGroupModal, setShowPurgeGroupModal] = useState(false);
  const [selectedGroupForPurge, setSelectedGroupForPurge] = useState<OobParticipantGroupDto | null>(null);
  const [purgeGroupParticipants, setPurgeGroupParticipants] = useState(true);
  const [archiveGroupAfterPurge, setArchiveGroupAfterPurge] = useState(true);

  const activeEvents = useMemo(() => events.filter((e) => e.status === "Active"), [events]);
  const activeEvent = useMemo(() => events.find((e) => e.status === "Active") || null, [events]);
  const adminSetupIncomplete = useMemo(
    () => !adminEmergencyProfile?.isConfigured
      || !adminEmergencyProfile?.hasSuccessfulBreakGlassTest,
    [adminEmergencyProfile]
  );
  const hasParticipants = (readinessDashboard?.totalParticipants ?? 0) > 0;
  const hasArtifacts = artifactLibrary.length > 0;
  const hasEvents = events.length > 0;
  const eventTypes = useMemo(() => Array.from(new Set(events.map((e) => e.eventType))).sort(), [events]);
  const eventStatuses = useMemo(() => Array.from(new Set(events.map((e) => e.status))).sort(), [events]);
  const fileEventGroups = useMemo(() => {
    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - 6);

    const alphabetical = (a: OobEventDto, b: OobEventDto) =>
      a.eventName.localeCompare(b.eventName, undefined, { sensitivity: "base" });

    const nonClosedRecent = events
      .filter((evt) => evt.status !== "Closed")
      .sort(alphabetical);

    const closedRecent = events
      .filter((evt) => evt.status === "Closed" && new Date(evt.createDt) >= cutoff)
      .sort(alphabetical);

    const recent = [...nonClosedRecent, ...closedRecent];
    const olderClosed = events
      .filter((evt) => evt.status === "Closed" && new Date(evt.createDt) < cutoff)
      .sort(alphabetical);

    return { recent, olderClosed };
  }, [events]);
  const eventFileFolders = useMemo(
    () => Array.from(new Set(eventArtifacts.map((artifact) => artifact.artifactType || "Other"))).sort(),
    [eventArtifacts]
  );
  const visibleEventArtifacts = useMemo(
    () => eventFileFolder === "All Files" ? eventArtifacts : eventArtifacts.filter((artifact) => (artifact.artifactType || "Other") === eventFileFolder),
    [eventArtifacts, eventFileFolder]
  );

  const filteredEvents = useMemo(
    () =>
      events.filter((evt) => {
        const typeMatches = eventTypeFilter === "All" || evt.eventType === eventTypeFilter;
        const statusMatches = eventStatusFilter === "All" || evt.status === eventStatusFilter;
        return typeMatches && statusMatches;
      }),
    [events, eventStatusFilter, eventTypeFilter]
  );
  const activeMembersInTargetGroup = useMemo(() => {
    if (!targetGroupForNewMember) return [];
    return (groupMembersByGroup[targetGroupForNewMember.groupGuid] || []).filter((member) => member.isActive);
  }, [groupMembersByGroup, targetGroupForNewMember]);
  const activeMemberGuidsInTargetGroup = useMemo(
    () => new Set(activeMembersInTargetGroup.map((member) => member.participantGuid)),
    [activeMembersInTargetGroup]
  );
  const filteredAvailableParticipants = useMemo(() => {
    const query = existingParticipantSearch.trim().toLowerCase();
    return participants.filter((participant) => participant.isActive
      && !activeMemberGuidsInTargetGroup.has(participant.participantGuid)
      && (!query || participant.displayName.toLowerCase().includes(query) || participant.email.toLowerCase().includes(query)));
  }, [activeMemberGuidsInTargetGroup, existingParticipantSearch, participants]);
  const matchingActiveParticipantsForNewEmail = useMemo(() => {
    const normalizedEmail = participantForm.email.trim().toLowerCase();
    if (!normalizedEmail) return [];
    return participants.filter((participant) => participant.isActive && participant.email.trim().toLowerCase() === normalizedEmail);
  }, [participantForm.email, participants]);

  useEffect(() => {
    const query = employeeSearch.trim();
    if (!showAddMemberModal || addMemberMode !== "new" || selectedDirectoryEmployee || query.length < 2) {
      setEmployeeSearchResults([]);
      setEmployeeSearchLoading(false);
      return;
    }

    let cancelled = false;
    setEmployeeSearchLoading(true);
    const timer = window.setTimeout(() => {
      void searchOobEmployeeDirectory(query)
        .then((results) => {
          if (!cancelled) setEmployeeSearchResults(results);
        })
        .catch((err: any) => {
          if (!cancelled) {
            setEmployeeSearchResults([]);
            setError(getApiErrorMessage(err, "Employee directory lookup failed."));
          }
        })
        .finally(() => {
          if (!cancelled) setEmployeeSearchLoading(false);
        });
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [addMemberMode, employeeSearch, selectedDirectoryEmployee, showAddMemberModal]);
  const filteredAnnouncements = useMemo(
    () =>
      announcementRows.filter((row) => {
        const search = announcementSearch.trim().toLowerCase();
        const searchMatches =
          !search ||
          row.title.toLowerCase().includes(search) ||
          row.message.toLowerCase().includes(search) ||
          row.createdBy.toLowerCase().includes(search);

        return searchMatches;
      }),
    [announcementRows, announcementSearch]
  );
  const filteredReadinessParticipants = useMemo(
    () => {
      const participantGroupsByGuid = new Map(participants.map((p) => [p.participantGuid, p.groupGuids || []]));
      const now = new Date();
      const startOfWeek = new Date(now);
      const weekDay = now.getDay();
      const offset = weekDay === 0 ? 6 : weekDay - 1;
      startOfWeek.setDate(now.getDate() - offset);
      startOfWeek.setHours(0, 0, 0, 0);

      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const quarterMonth = Math.floor(now.getMonth() / 3) * 3;
      const startOfQuarter = new Date(now.getFullYear(), quarterMonth, 1);
      const last7Days = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const last30Days = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      const last90Days = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);

      return (readinessDashboard?.participants || []).filter((p) => {
        const q = readinessSearch.trim().toLowerCase();
        const searchMatches =
          !q ||
          p.displayName.toLowerCase().includes(q) ||
          p.email.toLowerCase().includes(q) ||
          p.readinessState.toLowerCase().includes(q);

        const groupGuids = participantGroupsByGuid.get(p.participantGuid) || [];
        const groupMatches = readinessGroupFilter === "All" || groupGuids.includes(readinessGroupFilter);

        const stateMatches = readinessStateFilter === "All" || p.readinessState === readinessStateFilter;

        const preferredMatches = readinessPreferredFilter === "All" || p.preferredContactMethod === readinessPreferredFilter;

        const verificationMatches = (() => {
          if (readinessVerificationFilter === "All") return true;
          if (readinessVerificationFilter === "FullyVerified") return p.personalEmailVerified && p.personalPhoneVerified;
          if (readinessVerificationFilter === "MissingEither") return !p.personalEmailVerified || !p.personalPhoneVerified;
          if (readinessVerificationFilter === "MissingEmail") return !p.personalEmailVerified;
          if (readinessVerificationFilter === "MissingText") return !p.personalPhoneVerified;
          if (readinessVerificationFilter === "OnboardingNotSent") return !p.onboardingInviteSentDt;
          return true;
        })();

        const addedDate = new Date(p.createDt);
        const addedWindowMatches = (() => {
          if (readinessAddedWindowFilter === "All") return true;
          if (readinessAddedWindowFilter === "ThisWeek") return addedDate >= startOfWeek;
          if (readinessAddedWindowFilter === "ThisMonth") return addedDate >= startOfMonth;
          if (readinessAddedWindowFilter === "ThisQuarter") return addedDate >= startOfQuarter;
          return true;
        })();

        const inviteAgeMatches = (() => {
          if (readinessInviteAgeFilter === "All") return true;
          if (!p.onboardingInviteSentDt) return readinessInviteAgeFilter === "NotSent";

          const sentAt = new Date(p.onboardingInviteSentDt);
          const ageMs = now.getTime() - sentAt.getTime();
          const dayMs = 24 * 60 * 60 * 1000;

          if (readinessInviteAgeFilter === "LessThan1Day") return ageMs < dayMs;
          if (readinessInviteAgeFilter === "Between1And7Days") return ageMs >= dayMs && ageMs <= 7 * dayMs;
          if (readinessInviteAgeFilter === "Over7Days") return ageMs > 7 * dayMs;
          return true;
        })();

        const activityMatches = (() => {
          if (readinessActivityFilter === "All") return true;
          const lastUpdate = new Date(p.lastUpdateDt);
          if (readinessActivityFilter === "Last7Days") return lastUpdate >= last7Days;
          if (readinessActivityFilter === "Last30Days") return lastUpdate >= last30Days;
          if (readinessActivityFilter === "Last90Days") return lastUpdate >= last90Days;
          if (readinessActivityFilter === "OlderThan90Days") return lastUpdate < last90Days;
          return true;
        })();

        const groupCountMatches = (() => {
          if (readinessGroupCountFilter === "All") return true;
          if (readinessGroupCountFilter === "SingleGroup") return p.groupCount === 1;
          if (readinessGroupCountFilter === "MultiGroup") return p.groupCount > 1;
          if (readinessGroupCountFilter === "NoGroup") return p.groupCount === 0;
          return true;
        })();

        const isReadyState = (p.readinessState || "").trim().toLowerCase() === "ready";
        const needsActionMatches = !readinessNeedsActionOnly || !isReadyState;

        return searchMatches
          && groupMatches
          && stateMatches
          && preferredMatches
          && verificationMatches
          && addedWindowMatches
          && inviteAgeMatches
          && activityMatches
          && groupCountMatches
          && needsActionMatches;
      });
    },
    [
      participants,
      readinessDashboard,
      readinessSearch,
      readinessGroupFilter,
      readinessStateFilter,
      readinessVerificationFilter,
      readinessPreferredFilter,
      readinessAddedWindowFilter,
      readinessNeedsActionOnly,
      readinessInviteAgeFilter,
      readinessActivityFilter,
      readinessGroupCountFilter,
    ]
  );

  const readinessStates = useMemo(
    () => Array.from(new Set((readinessDashboard?.participants || []).map((p) => p.readinessState))).sort(),
    [readinessDashboard]
  );

  const handleInviteSecondaryIdentity = async (participantGuid: string) => {
    try {
      setInvitingParticipantGuid(participantGuid);
      await inviteOobSecondaryIdentity(participantGuid);
      await refreshReadinessDashboard();
      setSuccess("Secondary Tenant invitation started.");
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Failed to start Secondary Tenant invitation.");
    } finally {
      setInvitingParticipantGuid(null);
    }
  };
  const auditSeverities = useMemo(
    () => Array.from(new Set(audit.map((a) => a.severity))).filter(Boolean).sort(),
    [audit]
  );
  const auditTypes = useMemo(
    () => Array.from(new Set(audit.map((a) => a.auditType))).filter(Boolean).sort(),
    [audit]
  );
  const auditUsers = useMemo(
    () => Array.from(new Set(audit.map((a) => a.actionUser))).filter(Boolean).sort(),
    [audit]
  );
  const filteredAudit = useMemo(() => {
    const now = Date.now();

    return audit.filter((item) => {
      const q = auditSearch.trim().toLowerCase();
      const searchMatches =
        !q ||
        item.auditType.toLowerCase().includes(q) ||
        item.severity.toLowerCase().includes(q) ||
        item.actionUser.toLowerCase().includes(q) ||
        item.message.toLowerCase().includes(q);

      const severityMatches = auditSeverityFilter === "All" || item.severity === auditSeverityFilter;
      const typeMatches = auditTypeFilter === "All" || item.auditType === auditTypeFilter;
      const userMatches = auditUserFilter === "All" || item.actionUser === auditUserFilter;

      const timeMatches = (() => {
        if (auditTimeFilter === "All") return true;
        const itemTs = new Date(item.actionTimestamp).getTime();
        if (Number.isNaN(itemTs)) return false;
        const ageMs = now - itemTs;
        if (auditTimeFilter === "Last1Hour") return ageMs <= 60 * 60 * 1000;
        if (auditTimeFilter === "Last24Hours") return ageMs <= 24 * 60 * 60 * 1000;
        if (auditTimeFilter === "Last7Days") return ageMs <= 7 * 24 * 60 * 60 * 1000;
        if (auditTimeFilter === "Last30Days") return ageMs <= 30 * 24 * 60 * 60 * 1000;
        return true;
      })();

      const issuesOnlyMatches = !auditIssuesOnly || item.severity === "Warning" || item.severity === "Error";

      return searchMatches && severityMatches && typeMatches && userMatches && timeMatches && issuesOnlyMatches;
    });
  }, [audit, auditSearch, auditSeverityFilter, auditTypeFilter, auditUserFilter, auditTimeFilter, auditIssuesOnly]);

  const sortedAudit = useMemo(() => [...filteredAudit].sort((left, right) => {
    const leftValue = auditSortColumn === "time"
      ? new Date(left.actionTimestamp).getTime()
      : (auditSortColumn === "type" ? left.auditType : auditSortColumn === "severity" ? left.severity : left.actionUser).toLowerCase();
    const rightValue = auditSortColumn === "time"
      ? new Date(right.actionTimestamp).getTime()
      : (auditSortColumn === "type" ? right.auditType : auditSortColumn === "severity" ? right.severity : right.actionUser).toLowerCase();
    const comparison = typeof leftValue === "number" && typeof rightValue === "number"
      ? leftValue - rightValue
      : String(leftValue).localeCompare(String(rightValue));
    return auditSortAscending ? comparison : -comparison;
  }), [filteredAudit, auditSortAscending, auditSortColumn]);

  const toggleAuditSort = (column: AuditSortColumn) => {
    if (auditSortColumn === column) {
      setAuditSortAscending((ascending) => !ascending);
      return;
    }
    setAuditSortColumn(column);
    setAuditSortAscending(column === "time");
  };

  const activeAuditFilters = useMemo(() => {
    const chips: Array<{ key: string; label: string }> = [];
    if (auditSearch.trim()) chips.push({ key: "search", label: `Search: ${auditSearch.trim()}` });
    if (auditSeverityFilter !== "All") chips.push({ key: "severity", label: `Severity: ${auditSeverityFilter}` });
    if (auditTypeFilter !== "All") chips.push({ key: "type", label: `Type: ${auditTypeFilter}` });
    if (auditUserFilter !== "All") chips.push({ key: "user", label: `User: ${auditUserFilter}` });
    if (auditTimeFilter !== "All") chips.push({ key: "time", label: `Time: ${auditTimeFilter}` });
    if (auditIssuesOnly) chips.push({ key: "issues", label: "Issues Only" });
    return chips;
  }, [auditSearch, auditSeverityFilter, auditTypeFilter, auditUserFilter, auditTimeFilter, auditIssuesOnly]);

  const handleRemoveAuditFilter = (filterKey: string) => {
    if (filterKey === "search") setAuditSearch("");
    if (filterKey === "severity") setAuditSeverityFilter("All");
    if (filterKey === "type") setAuditTypeFilter("All");
    if (filterKey === "user") setAuditUserFilter("All");
    if (filterKey === "time") setAuditTimeFilter("All");
    if (filterKey === "issues") setAuditIssuesOnly(false);
  };

  const participantMetaByGuid = useMemo(
    () => new Map(participants.map((p) => [p.participantGuid, p])),
    [participants]
  );

  const acknowledgedParticipantGuids = useMemo(
    () => new Set(acks.filter((a) => (a.acknowledgementCount ?? 0) > 0).map((a) => a.participantGuid)),
    [acks]
  );
  const acknowledgementPercent = eventParticipants.length === 0
    ? 0
    : Math.round((acknowledgedParticipantGuids.size / eventParticipants.length) * 100);

  useEffect(() => {
    const updateBreakGlassTimer = () => {
      const raw = localStorage.getItem("oob-admin-session");
      if (!raw) {
        setAdminSessionRemainingMs(null);
        setAdminSessionWarning(null);
        return;
      }

      try {
        const session = JSON.parse(raw) as { sessionExpiresAt?: string };
        if (!session.sessionExpiresAt) {
          setAdminSessionRemainingMs(null);
          setAdminSessionWarning(null);
          return;
        }

        const remainingMs = new Date(session.sessionExpiresAt).getTime() - Date.now();
        if (remainingMs <= 0) {
          localStorage.removeItem("oob-admin-session");
          setAdminSessionRemainingMs(0);
          setAdminSessionWarning("Break-glass admin session expired. Redirecting to re-authenticate.");
          window.location.href = "/admin/oob/breakglass";
          return;
        }

        setAdminSessionRemainingMs(remainingMs);

        const minutesRemaining = Math.floor(remainingMs / 60000);
        if (minutesRemaining <= 10) {
          setAdminSessionWarning(`Break-glass session expires in ${minutesRemaining} minute${minutesRemaining !== 1 ? "s" : ""}.`);
        } else if (minutesRemaining <= 30) {
          setAdminSessionWarning(`Break-glass session active. ${minutesRemaining} minute${minutesRemaining !== 1 ? "s" : ""} remaining.`);
        } else {
          setAdminSessionWarning(null);
        }
      } catch {
        setAdminSessionRemainingMs(null);
        setAdminSessionWarning(null);
      }
    };

    updateBreakGlassTimer();
    const intervalId = window.setInterval(updateBreakGlassTimer, 30000);
    return () => window.clearInterval(intervalId);
  }, []);

  const lifecycleCounts = useMemo(() => {
    const updates = selectedEvent?.updates ?? [];
    const counts = {
      published: 0,
      retracted: 0,
      archived: 0,
    };

    for (const update of updates) {
      const lifecycle = getUpdateLifecycle(update);
      if (lifecycle === "Published") counts.published += 1;
      if (lifecycle === "Retracted") counts.retracted += 1;
      if (lifecycle === "Archived") counts.archived += 1;
    }

    return counts;
  }, [selectedEvent?.updates]);

  const filteredUpdates = useMemo(() => {
    const updates = selectedEvent?.updates ?? [];
    return updates.filter((u) => {
      const lifecycle = getUpdateLifecycle(u);
      const isArchived = lifecycle === "Archived";
      const isRetracted = lifecycle === "Retracted";
      const isPublished = lifecycle === "Published";

      if (showActionableOnly && isArchived) {
        return false;
      }

      switch (updateLifecycleFilter) {
        case "Published":
          return isPublished;
        case "Retracted":
          return isRetracted;
        case "Archived":
          return isArchived;
        case "Active":
          return !isArchived;
        case "All":
        default:
          return true;
      }
    });
  }, [selectedEvent?.updates, showActionableOnly, updateLifecycleFilter]);

  const refreshParticipants = async () => {
    const data = await getOobParticipants();
    setParticipants(data);
  };

  const refreshReadinessDashboard = async () => {
    const data = await getOobReadinessDashboard();
    setReadinessDashboard(data);
  };

  const refreshAdminEmergencyProfile = async () => {
    const data = await getMyOobAdminEmergencyProfile();
    setAdminEmergencyProfile(data);
    setAdminEmergencyEmail(data.emergencyEmail || "");
    setAdminEmergencyPhone(data.verifiedPhone || "");
  };

  const handleRenewBreakGlassSession = async () => {
    try {
      setRenewingAdminSession(true);
      clearMessages();

      const renewed = await renewOobBreakGlassSession();
      const refreshed = {
        sessionToken: renewed.adminSessionToken,
        sessionType: "BreakGlassAdmin",
        sessionExpiresAt: renewed.sessionExpiresAt,
      };

      localStorage.setItem("oob-admin-session", JSON.stringify(refreshed));
      setAdminSessionRemainingMs(new Date(renewed.sessionExpiresAt).getTime() - Date.now());
      setAdminSessionWarning(null);
      setSuccess("Break-glass session renewed.");
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Failed to renew break-glass session.");
    } finally {
      setRenewingAdminSession(false);
    }
  };

  const handleSaveAdminEmergencyProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    clearMessages();
    setLoading(true);
    try {
      const updated = await updateMyOobAdminEmergencyProfile({
        emergencyEmail: adminEmergencyEmail,
        verifiedPhone: adminEmergencyPhone,
      });
      setAdminEmergencyProfile(updated);
      setShowAdminEmergencyModal(false);
      setSuccess("Emergency access profile saved. Next step: run a break-glass test.");
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Failed to save emergency access profile.");
    } finally {
      setLoading(false);
    }
  };

  const refreshEvents = async () => {
    const data = await getOobEvents();
    setEvents(data);
  };

  const refreshGroups = async () => {
    const data = await getOobParticipantGroups();
    setGroups(data);
  };

  const refreshAnnouncements = async () => {
    const data = await getAdminOobPlatformAnnouncements(0, 100);
    const rows: PlatformAnnouncementRow[] = data.announcements.map((ann) => ({
      announcementGuid: ann.announcementGuid,
      title: ann.title,
      message: ann.message,
      createdBy: ann.createdBy,
      recipientCount: ann.recipientCount,
      queuedCount: ann.queuedCount,
      createDt: ann.createDt,
    }));
    setAnnouncementRows(rows.sort((a, b) => new Date(b.createDt).getTime() - new Date(a.createDt).getTime()));
  };

  const refreshArtifactLibrary = async () => {
    const data = await getOobArtifactLibrary();
    setArtifactLibrary(data);
  };

  const refreshEventArtifacts = async (eventGuid: string) => {
    const data = await getOobEventArtifacts(eventGuid);
    setEventArtifacts(data);

    try {
      const latestManifest = await getOobLatestEventArtifactManifest(eventGuid);
      setEventArtifactManifest(latestManifest);
    } catch {
      setEventArtifactManifest(null);
    }
  };

  const refreshSelectedEvent = async (eventGuid: string) => {
    const data = await getOobEvent(eventGuid, true);
    setSelectedEvent(data);

    if (data.deliveryStatus === "NotStarted" || data.deliveryStatus === "LocalActive") {
      setSecondaryProvisioningStatus(null);
    } else {
      try {
        setSecondaryProvisioningStatus(await getOobSecondaryProvisioningStatus(eventGuid));
      } catch {
        setSecondaryProvisioningStatus(null);
      }
    }

    try {
      setEventFollowUps(await getOobEventFollowUps(eventGuid));
    } catch {
      setEventFollowUps([]);
    }

    const [ackData, auditData, participantData, artifactData] = await Promise.all([
      getOobAcknowledgementSummary(eventGuid),
      getOobAudit(eventGuid, 0, 300),
      getOobEventParticipants(eventGuid),
      getOobEventArtifacts(eventGuid),
    ]);

    setAcks(ackData);
    setAudit(auditData);
    setEventParticipants(participantData);
    setEventArtifacts(artifactData);
    try {
      setEventQuestions(await getOobAdminEventQuestions(eventGuid));
    } catch {
      setEventQuestions([]);
    }

    try {
      const channels = await getOobAdminLocalEventChannels(eventGuid);
      setLocalEventChannels(channels);
      setSelectedLocalChannelGuid((current) => current && channels.some((channel) => channel.channelGuid === current) ? current : (channels[0]?.channelGuid || ""));
    } catch {
      setLocalEventChannels([]);
      setSelectedLocalChannelGuid("");
      setLocalChannelMessages([]);
    }
  };

  const loadLocalChannelMessages = async (channelGuid: string) => {
    if (!selectedEventGuid || !channelGuid) {
      setLocalChannelMessages([]);
      return;
    }
    try {
      setLocalChannelMessages(await getOobAdminLocalEventChannelMessages(selectedEventGuid, channelGuid));
    } catch (err: any) {
      setLocalChannelMessages([]);
      setError(getApiErrorMessage(err, "Failed to load local channel messages."));
    }
  };

  const handleSelectLocalChannel = async (channelGuid: string) => {
    setSelectedLocalChannelGuid(channelGuid);
    await loadLocalChannelMessages(channelGuid);
  };

  const handlePostLocalChannelMessage = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedEventGuid || !selectedLocalChannelGuid || !localChannelMessageBody.trim()) return;
    try {
      setPostingLocalChannelMessage(true);
      await postOobAdminLocalEventChannelMessage(selectedEventGuid, selectedLocalChannelGuid, { messageBody: localChannelMessageBody.trim() });
      setLocalChannelMessageBody("");
      await loadLocalChannelMessages(selectedLocalChannelGuid);
      setLocalEventChannels(await getOobAdminLocalEventChannels(selectedEventGuid));
    } catch (err: any) {
      setError(getApiErrorMessage(err, "Failed to post local channel message."));
    } finally {
      setPostingLocalChannelMessage(false);
    }
  };

  useEffect(() => {
    if (selectedLocalChannelGuid) {
      void loadLocalChannelMessages(selectedLocalChannelGuid);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocalChannelGuid]);

  const handlePublishQuestion = async (question: OobEventQuestionDto) => {
    if (!selectedEventGuid || !questionAnswer[question.questionGuid]?.trim()) return;
    clearMessages();
    try {
      setPublishingQuestionGuid(question.questionGuid);
      await publishOobAdminEventQuestion(selectedEventGuid, question.questionGuid, {
        answerText: questionAnswer[question.questionGuid].trim(),
        importance: "Normal",
        isPinned: false,
      });
      setSuccess("Question answered and published to the event Q&A.");
      await refreshSelectedEvent(selectedEventGuid);
    } catch (err: any) {
      setError(getApiErrorMessage(err, "Failed to publish event question."));
    } finally {
      setPublishingQuestionGuid(null);
    }
  };

  const handleSaveEventQuestion = async () => {
    if (!selectedEventGuid || !editEventQuestion || !editEventQuestion.questionText.trim()) return;
    clearMessages();
    try {
      setLoading(true);
      await editOobAdminEventQuestion(selectedEventGuid, editEventQuestion.questionGuid, {
        questionText: editEventQuestion.questionText.trim(),
        answerText: editEventQuestion.answerText?.trim() || undefined,
        displayAsAnonymous: editEventQuestion.displayAsAnonymous,
        importance: editEventQuestion.importance,
      });
      setEditEventQuestion(null);
      setSuccess("Event question updated.");
      await refreshSelectedEvent(selectedEventGuid);
    } catch (err: any) {
      setError(getApiErrorMessage(err, "Failed to update event question."));
    } finally {
      setLoading(false);
    }
  };

  const handleRetrySecondaryProvisioning = async () => {
    if (!selectedEventGuid) return;

    try {
      setSecondaryProvisioningLoading(true);
      setError(null);
      const status = await retryOobSecondaryProvisioning(selectedEventGuid);
      setSecondaryProvisioningStatus(status);
      setSuccess("Secondary Tenant provisioning retry completed.");
      await refreshSelectedEvent(selectedEventGuid);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Secondary Tenant provisioning retry failed.");
    } finally {
      setSecondaryProvisioningLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedEventGuid) {
      return;
    }

    const load = async () => {
      try {
        await refreshSelectedEvent(selectedEventGuid);
      } catch (err: any) {
        setError(getApiErrorMessage(err, "Failed to load selected event."));
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateLifecycleFilter]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      setError(null);
      try {
        await Promise.all([
          refreshParticipants(),
          refreshEvents(),
          refreshGroups(),
          refreshAnnouncements(),
          refreshReadinessDashboard(),
          refreshAdminEmergencyProfile(),
        ]);
      } catch (err: any) {
        setError(err?.response?.data?.error || err?.message || "Failed to load OOB data.");
      } finally {
        setLoading(false);
      }
    };

    init();
  }, []);

  useEffect(() => {
    if (!loading && adminSetupIncomplete) {
      setShowAdminGettingStarted(true);
    }
  }, [loading, adminSetupIncomplete]);

  useEffect(() => {
    setReconcileCheckedClean(false);
    if (!selectedEventGuid) {
      setSelectedEvent(null);
      setAcks([]);
      setAudit([]);
      setEventParticipants([]);
      return;
    }

    const load = async () => {
      try {
        await refreshSelectedEvent(selectedEventGuid);
      } catch (err: any) {
        setError(err?.response?.data?.error || err?.message || "Failed to load selected event.");
      }
    };

    load();
  }, [selectedEventGuid]);

  useEffect(() => {
    if (tab !== "files") {
      return;
    }

    const load = async () => {
      try {
        setLoading(true);
        await refreshArtifactLibrary();
      } catch (err: any) {
        setError(getApiErrorMessage(err, "Failed to load artifact files."));
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [tab]);

  useEffect(() => {
    if (tab !== "files" || filesSubTab !== "eventFiles") {
      return;
    }

    if (!filesEventGuid) {
      setEventArtifacts([]);
      setEventArtifactManifest(null);
      return;
    }

    const load = async () => {
      try {
        setLoading(true);
        await refreshEventArtifacts(filesEventGuid);
      } catch (err: any) {
        setError(getApiErrorMessage(err, "Failed to load event artifacts."));
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [tab, filesSubTab, filesEventGuid]);

  useEffect(() => {
    if (selectedEvent) {
      setEventSubTab("updates");
    }
  }, [selectedEvent?.eventGuid]);

  useEffect(() => {
    if (activeEvent && !autoFocusedActiveEvent) {
      setSelectedEventGuid(activeEvent.eventGuid);
      setTab("activeEvent");
      setAutoFocusedActiveEvent(true);
      return;
    }

    if (!activeEvent && autoFocusedActiveEvent) {
      setAutoFocusedActiveEvent(false);
    }
  }, [activeEvent, autoFocusedActiveEvent, tab]);

  const clearMessages = () => {
    setError(null);
    setSuccess(null);
  };

  const handleRemoveParticipantFromGroup = async (groupGuid: string, participantGuid: string) => {
    clearMessages();
    setLoading(true);
    try {
      await removeOobParticipantFromGroup(groupGuid, participantGuid);
      setSuccess("Member removed from group.");
      await Promise.all([refreshParticipants(), refreshGroups(), ensureGroupMembersLoaded(groupGuid, true), refreshReadinessDashboard()]);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Failed to remove member from group.");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGroup = async (e: React.FormEvent) => {
    e.preventDefault();
    clearMessages();

    if (!groupForm.groupName.trim()) {
      setError("Group name is required.");
      return;
    }

    setLoading(true);
    try {
      await createOobParticipantGroup({
        groupName: groupForm.groupName.trim(),
        sortOrder: Number(groupForm.sortOrder) || 0,
        description: groupForm.description?.trim() || undefined,
      });

      setSuccess("Group created.");
      setGroupForm({ groupName: "", sortOrder: 0, description: "" });
      setShowCreateGroupModal(false);
      await refreshGroups();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Failed to create group.");
    } finally {
      setLoading(false);
    }
  };

  const openAddMemberModal = async (group: OobParticipantGroupDto) => {
    clearMessages();
    try {
      await ensureGroupMembersLoaded(group.groupGuid, true);
    } catch (err: any) {
      setError(getApiErrorMessage(err, "Failed to load group members."));
      return;
    }
    setTargetGroupForNewMember(group);
    setAddMemberMode("existing");
    setExistingParticipantSearch("");
    setSelectedExistingParticipantGuid("");
    setIsExistingParticipantMenuOpen(false);
    setEmployeeSearch("");
    setEmployeeSearchResults([]);
    setSelectedDirectoryEmployee(null);
    setParticipantForm({
      ...defaultParticipant,
      isDefaultParticipant: true,
      groupGuids: [group.groupGuid],
    });
    setShowAddMemberModal(true);
  };

  const handleSelectDirectoryEmployee = (employee: OobEmployeeLookupDto) => {
    setSelectedDirectoryEmployee(employee);
    setParticipantForm((current) => ({
      ...current,
      employeeId: employee.employeeId || "",
      email: employee.email,
      displayName: employee.displayName || "",
      department: employee.department || "",
    }));
    setEmployeeSearch(`${employee.displayName} · ${employee.email}`);
    setEmployeeSearchResults([]);
    clearMessages();
  };

  const handleAddSelectedExistingParticipant = async () => {
    const participant = participants.find((item) => item.participantGuid === selectedExistingParticipantGuid);
    if (!participant) {
      setError("Choose an existing participant first.");
      return;
    }
    await handleAddKnownParticipantToTargetGroup(participant);
  };

  const handleAddKnownParticipantToTargetGroup = async (participant: OobParticipantDto) => {
    if (!targetGroupForNewMember || !participant.isActive) return;
    if (participant.groupGuids.includes(targetGroupForNewMember.groupGuid)) {
      setError(`${participant.displayName} is already a member of ${targetGroupForNewMember.groupName}.`);
      return;
    }

    clearMessages();
    setLoading(true);
    try {
      await updateOobParticipantGroups(participant.participantGuid, {
        groupGuids: [...new Set([...(participant.groupGuids || []), targetGroupForNewMember.groupGuid])],
      });
      setSuccess(`${participant.displayName} added to ${targetGroupForNewMember.groupName}.`);
      setShowAddMemberModal(false);
      setTargetGroupForNewMember(null);
      setEmployeeSearch("");
      setEmployeeSearchResults([]);
      await Promise.all([refreshParticipants(), refreshGroups(), refreshReadinessDashboard()]);
      await ensureGroupMembersLoaded(targetGroupForNewMember.groupGuid, true);
    } catch (err: any) {
      setError(getApiErrorMessage(err, "Failed to add participant to group."));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateParticipantInGroup = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!targetGroupForNewMember) {
      setError("Select a target group first.");
      return;
    }

    if (!selectedDirectoryEmployee) {
      setError("Search the employee directory and select a person before creating a new OOB participant.");
      return;
    }

    if (matchingActiveParticipantsForNewEmail.length > 0) {
      setError(matchingActiveParticipantsForNewEmail.length > 1
        ? "Multiple active participant records already use this email. Resolve the duplicates before adding anyone."
        : activeMemberGuidsInTargetGroup.has(matchingActiveParticipantsForNewEmail[0].participantGuid)
          ? "This email already belongs to the target group."
          : "This person already exists. Switch to Add existing to add them to this group.");
      if (matchingActiveParticipantsForNewEmail.length === 1 && !activeMemberGuidsInTargetGroup.has(matchingActiveParticipantsForNewEmail[0].participantGuid)) {
        setSelectedExistingParticipantGuid(matchingActiveParticipantsForNewEmail[0].participantGuid);
      }
      return;
    }

    const payload: OobCreateParticipantRequest = {
      employeeId: participantForm.employeeId,
      email: participantForm.email,
      displayName: participantForm.displayName,
      department: participantForm.department || undefined,
      location: participantForm.location || undefined,
      region: participantForm.region || undefined,
      phoneNumber: participantForm.phoneNumber || undefined,
      isDefaultParticipant: true,
      groupGuids: [targetGroupForNewMember.groupGuid],
    };

    clearMessages();
    setLoading(true);
    try {
      await createOobParticipant(payload);
      setSuccess(`Member added to ${targetGroupForNewMember.groupName}.`);
      setShowAddMemberModal(false);
      setTargetGroupForNewMember(null);
      setParticipantForm(defaultParticipant);
      await Promise.all([refreshParticipants(), refreshGroups()]);
      await ensureGroupMembersLoaded(payload.groupGuids[0], true);
    } catch (err: any) {
      setError(getApiErrorMessage(err, "Failed to add member to group."));
    } finally {
      setLoading(false);
    }
  };

  const handleUploadArtifact = async () => {
    if (!artifactUploadFile) {
      setError("Select a file to upload.");
      return;
    }

    clearMessages();
    setLoading(true);
    try {
      const uploaded = await uploadOobArtifactToLibrary(artifactUploadFile, artifactUploadCategory);
      setSuccess(`Uploaded artifact: ${uploaded.artifactName}`);
      setArtifactUploadFile(null);
      setArtifactFileInputKey((k) => k + 1);
      await refreshArtifactLibrary();
    } catch (err: any) {
      setError(getApiErrorMessage(err, "Failed to upload artifact."));
    } finally {
      setLoading(false);
    }
  };

  const handleArchiveArtifact = async (artifactGuid: string) => {
    clearMessages();
    setLoading(true);
    try {
      await archiveOobArtifactFromLibrary(artifactGuid);
      setSuccess("Artifact archived.");
      await refreshArtifactLibrary();
    } catch (err: any) {
      setError(getApiErrorMessage(err, "Failed to archive artifact."));
    } finally {
      setLoading(false);
    }
  };

  const handleLoadEventArtifacts = async () => {
    if (!filesEventGuid) {
      setError("Select an event first.");
      return;
    }

    clearMessages();
    setLoading(true);
    try {
      await refreshEventArtifacts(filesEventGuid);
      setSuccess("Event artifacts refreshed.");
    } catch (err: any) {
      setError(getApiErrorMessage(err, "Failed to load event artifacts."));
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadLibraryArtifact = async (artifact: OobArtifactLibraryItemDto) => {
    clearMessages();
    try {
      await downloadOobLibraryArtifact(artifact);
    } catch (err: any) {
      setError(getApiErrorMessage(err, "Failed to download library file."));
    }
  };

  const handleDownloadEventArtifact = async (artifact: OobEventArtifactDto, eventGuid = filesEventGuid) => {
    if (!eventGuid) return;
    clearMessages();
    try {
      await downloadOobAdminEventArtifact(eventGuid, artifact.artifactGuid, artifact.artifactName);
    } catch (err: any) {
      setError(getApiErrorMessage(err, "Failed to download event file."));
    }
  };

  const handleRefreshArtifactLibrary = async () => {
    clearMessages();
    setLoading(true);
    try {
      await refreshArtifactLibrary();
    } catch (err: any) {
      setError(getApiErrorMessage(err, "Failed to refresh artifact library."));
    } finally {
      setLoading(false);
    }
  };

  const toggleParticipantGroupExpanded = async (groupGuid: string) => {
    clearMessages();
    try {
      if (!expandedParticipantGroups.includes(groupGuid)) {
        await ensureGroupMembersLoaded(groupGuid);
      }

      setExpandedParticipantGroups((prev) =>
        prev.includes(groupGuid) ? prev.filter((g) => g !== groupGuid) : [...prev, groupGuid]
      );
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Failed to load group members.");
    }
  };

  const handleBulkImport = async () => {
    if (!bulkFile) {
      setError("Please select a CSV file first.");
      return;
    }

    if (!selectedBulkGroupGuid) {
      setError("Please select a target group for bulk import.");
      return;
    }

    clearMessages();
    setLoading(true);
    try {
      const res = await bulkImportOobParticipants(bulkFile, bulkMode, selectedBulkGroupGuid);
      setSuccess(`Import complete: ${res.succeeded} succeeded, ${res.failed} failed out of ${res.totalRows}.`);
      await Promise.all([refreshParticipants(), refreshGroups()]);
      await ensureGroupMembersLoaded(selectedBulkGroupGuid, true);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Bulk import failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadBulkTemplate = () => {
    const header = ["employeeId", "email", "displayName", "department", "phoneNumber"];
    const sample = ["E12345", "person@company.com", "Jane Doe", "Operations", "+1-555-0100"];
    const csv = `${header.join(",")}\n${sample.join(",")}\n`;
    const blob = new Blob(["\ufeff", csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");

    anchor.href = url;
    anchor.download = "oob-participants-template.csv";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  };

  const handlePurgeAll = async () => {
    if (!purgeAllOptions.purgeParticipants && !purgeAllOptions.purgeGroups) {
      setError("Select at least one purge option.");
      return;
    }

    clearMessages();
    setLoading(true);
    try {
      const res = await purgeOobAll(purgeAllOptions);
      setSuccess(
        `Purge complete. Participants: ${res.purgedParticipants}, groups: ${res.purgedGroups}, group memberships: ${res.purgedGroupMemberships}.`
      );
      setShowPurgeAllModal(false);
      setExpandedGroups([]);
      setExpandedParticipantGroups([]);
      setGroupMembersByGroup({});
      await Promise.all([refreshParticipants(), refreshGroups(), refreshEvents()]);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Purge failed.");
    } finally {
      setLoading(false);
    }
  };

  const openPurgeAllModal = () => {
    clearMessages();
    setPurgeAllOptions({ purgeParticipants: true, purgeGroups: false });
    setShowPurgeAllModal(true);
  };

  const openPurgeGroupModal = (group: OobParticipantGroupDto) => {
    clearMessages();
    setSelectedGroupForPurge(group);
    setPurgeGroupParticipants(true);
    setArchiveGroupAfterPurge(true);
    setShowPurgeGroupModal(true);
  };

  const handlePurgeGroup = async () => {
    if (!selectedGroupForPurge) {
      return;
    }

    clearMessages();
    setLoading(true);
    try {
      const res = await purgeOobGroup(selectedGroupForPurge.groupGuid, {
        purgeParticipants: purgeGroupParticipants,
        archiveGroup: archiveGroupAfterPurge,
      });
      setSuccess(
        `Group purge complete. Memberships removed: ${res.purgedMemberships}, participants purged: ${res.purgedParticipants}, group archived: ${res.groupArchived ? "yes" : "no"}.`
      );
      setShowPurgeGroupModal(false);
      setSelectedGroupForPurge(null);
      setExpandedGroups((prev) => prev.filter((g) => g !== res.groupGuid));
      setExpandedParticipantGroups((prev) => prev.filter((g) => g !== res.groupGuid));
      setGroupMembersByGroup((prev) => {
        const next = { ...prev };
        delete next[res.groupGuid];
        return next;
      });
      await Promise.all([refreshParticipants(), refreshGroups(), refreshEvents()]);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Failed to purge group.");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvent = async () => {
    clearMessages();
    setLoading(true);
    try {
      const created = await createOobEvent(eventForm);
      if (eventForm.createLocalEvent && created.deliveryStatus !== "LocalActive") {
        setEventForm(defaultEvent);
        setCreateEventStep(1);
        setShowCreateEventWizard(false);
        await refreshEvents();
        setSelectedEventGuid(created.eventGuid);
        setTab("manageEvents");
        setError("The event was created as a draft because the API did not honor local activation. Do not activate it until the API is updated.");
        return;
      }
      setSuccess("Event created.");
      setEventForm(defaultEvent);
      setCreateEventStep(1);
      setShowCreateEventWizard(false);
      await refreshEvents();
      setSelectedEventGuid(created.eventGuid);
      setTab("manageEvents");
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Failed to create event.");
    } finally {
      setLoading(false);
    }
  };

  const openCreateEventWizard = () => {
    clearMessages();
    setEventForm(defaultEvent);
    setCreateEventStep(1);
    setShowCreateEventWizard(true);
  };

  const closeCreateEventWizard = () => {
    setShowCreateEventWizard(false);
    setCreateEventStep(1);
  };

  const handleActivate = async () => {
    if (!selectedEventGuid) return;
    clearMessages();
    setLoading(true);
    setActivatingEventGuid(selectedEventGuid);
    try {
      const result = await activateOobEvent(selectedEventGuid);
      if (result.requiresLocalPivot) {
        setSelectedEvent(result.event);
        setPivotEventForm({
          eventName: result.event.eventName,
          eventType: result.event.eventType as OobCreateEventRequest["eventType"],
          safetyPhrase: result.event.safetyPhrase,
          description: result.event.description || "",
          createLocalEvent: false,
          groupGuids: result.event.groupGuids,
          isSilent: result.event.isSilent,
        });
        setShowLocalPivotModal(true);
        setError(null);
      } else {
        setSuccess("Event activated. Email notifications queued.");
      }
      await Promise.all([refreshEvents(), refreshSelectedEvent(selectedEventGuid)]);
      setTab("activeEvent");
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Failed to activate event.");
    } finally {
      setLoading(false);
      setActivatingEventGuid(null);
    }
  };

  const handleSavePivotReview = async () => {
    if (!selectedEventGuid) return;
    clearMessages();
    try {
      setSavingPivotReview(true);
      const updated = await updateOobPivotEvent(selectedEventGuid, pivotEventForm);
      setSelectedEvent(updated);
      setSuccess("Pivot review saved. Confirm local activation when ready.");
      await refreshSelectedEvent(selectedEventGuid);
    } catch (err: any) {
      setError(getApiErrorMessage(err, "Failed to save the pivot review."));
    } finally {
      setSavingPivotReview(false);
    }
  };

  const handleActivateLocalEventSpace = async () => {
    if (!selectedEventGuid) return;
    clearMessages();
    setLoading(true);
    try {
      await activateOobLocalEventSpace(selectedEventGuid);
      setShowLocalPivotModal(false);
      setSuccess("Local communication space activated. Personal-email invitations are queued.");
      await Promise.all([refreshEvents(), refreshSelectedEvent(selectedEventGuid)]);
      setTab("activeEvent");
    } catch (err: any) {
      setError(getApiErrorMessage(err, "Failed to activate the local communication space."));
    } finally {
      setLoading(false);
    }
  };

  const handleCloseEvent = async () => {
    if (!selectedEventGuid) return;
    clearMessages();
    setLoading(true);
    try {
      await closeOobEvent(selectedEventGuid);
      setSuccess("Event closed.");
      await refreshEvents();
      setSelectedEventGuid("");
      setSelectedEvent(null);
      setTab("manageEvents");
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Failed to close event.");
    } finally {
      setLoading(false);
    }
  };

  const handleResolveEvent = async () => {
    if (!selectedEventGuid) return;
    try {
      await resolveOobEvent(selectedEventGuid);
      setSuccess("Event resolved. Team access and content are preserved for review.");
      await Promise.all([refreshEvents(), refreshSelectedEvent(selectedEventGuid)]);
      setEventSubTab("closeout");
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Failed to resolve event.");
    }
  };

  const handleReconcileMembership = async () => {
    if (!selectedEventGuid) return;
    clearMessages();
    setLifecycleActionLoading(true);
    try {
      const followUps = await reconcileOobEventMembership(selectedEventGuid);
      setEventFollowUps(followUps);
      setReconcileCheckedClean(!followUps.some((f) => f.status === "Open"));
      setSuccess(
        followUps.length === 0
          ? "Reconciliation complete. No membership discrepancies found."
          : `Reconciliation complete. ${followUps.length} follow-up(s) require attention.`
      );
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Failed to reconcile membership.");
    } finally {
      setLifecycleActionLoading(false);
    }
  };

  const handleResolveFollowUp = async (followUpGuid: string) => {
    if (!selectedEventGuid) return;
    clearMessages();
    setResolvingFollowUpGuid(followUpGuid);
    try {
      await resolveOobEventFollowUp(selectedEventGuid, followUpGuid, followUpNotes[followUpGuid]);
      setSuccess("Follow-up resolved.");
      setEventFollowUps(await getOobEventFollowUps(selectedEventGuid));
      setFollowUpNotes((prev) => {
        const next = { ...prev };
        delete next[followUpGuid];
        return next;
      });
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Failed to resolve follow-up.");
    } finally {
      setResolvingFollowUpGuid(null);
    }
  };

  const handleFinalizeEvent = async () => {
    if (!selectedEventGuid) return;
    clearMessages();
    setLifecycleActionLoading(true);
    try {
      await finalizeOobEvent(selectedEventGuid);
      setSuccess("Event finalized.");
      await Promise.all([refreshEvents(), refreshSelectedEvent(selectedEventGuid)]);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Failed to finalize event.");
    } finally {
      setLifecycleActionLoading(false);
    }
  };

  const handleDeprovisionEvent = async () => {
    if (!selectedEventGuid) return;
    clearMessages();
    setLifecycleActionLoading(true);
    try {
      await deprovisionOobEvent(selectedEventGuid);
      setSuccess("Event deprovisioned. Participant access removed; guest identities retained.");
      await Promise.all([refreshEvents(), refreshSelectedEvent(selectedEventGuid)]);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Failed to deprovision event.");
    } finally {
      setLifecycleActionLoading(false);
    }
  };

  const handleResendPending = async () => {
    if (!selectedEventGuid) return;
    clearMessages();
    setLoading(true);
    try {
      const result = await resendOobPending(selectedEventGuid);
      setSuccess(`Queued ${result.queuedCount} reminder email(s) for pending participants.`);
      await refreshSelectedEvent(selectedEventGuid);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Failed to resend pending reminders.");
    } finally {
      setLoading(false);
    }
  };

  const handleCopyParticipantLink = async (eventGuid: string) => {
    clearMessages();
    try {
      const absoluteLink = `${window.location.origin}${participantPublicPath(eventGuid)}`;
      await navigator.clipboard.writeText(absoluteLink);
      setSuccess("Copied participant link.");
    } catch {
      setError("Unable to copy link. Your browser may block clipboard access.");
    }
  };

  const ensureGroupMembersLoaded = async (groupGuid: string, forceReload: boolean = false) => {
    if (!forceReload && groupMembersByGroup[groupGuid]) {
      return groupMembersByGroup[groupGuid];
    }

    const members = await getOobParticipantGroupMembers(groupGuid);
    setGroupMembersByGroup((prev) => ({ ...prev, [groupGuid]: members }));
    return members;
  };

  const toggleGroupExpanded = async (groupGuid: string) => {
    clearMessages();
    try {
      if (!expandedGroups.includes(groupGuid)) {
        await ensureGroupMembersLoaded(groupGuid);
      }

      setExpandedGroups((prev) =>
        prev.includes(groupGuid) ? prev.filter((g) => g !== groupGuid) : [...prev, groupGuid]
      );
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Failed to load group members.");
    }
  };

  const isGroupSelected = (groupGuid: string) => selectedAnnouncementGroups.includes(groupGuid);

  const isMemberSelected = (groupGuid: string, participantGuid: string) => {
    if (isGroupSelected(groupGuid)) {
      return !excludedAnnouncementParticipants.includes(participantGuid);
    }

    return includedAnnouncementParticipants.includes(participantGuid);
  };

  const toggleGroupSelected = async (groupGuid: string) => {
    clearMessages();
    try {
      const members = await ensureGroupMembersLoaded(groupGuid);
      const memberGuids = new Set(members.map((m) => m.participantGuid));

      setSelectedAnnouncementGroups((prev) => {
        const next = prev.includes(groupGuid) ? prev.filter((g) => g !== groupGuid) : [...prev, groupGuid];
        return next;
      });

      setExcludedAnnouncementParticipants((prev) => {
        if (isGroupSelected(groupGuid)) {
          return prev.filter((p) => !memberGuids.has(p));
        }

        return prev;
      });
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Failed to load group members.");
    }
  };

  const toggleMemberSelected = (groupGuid: string, participantGuid: string) => {
    const currentlySelected = isMemberSelected(groupGuid, participantGuid);

    if (isGroupSelected(groupGuid)) {
      setExcludedAnnouncementParticipants((prev) => {
        if (currentlySelected) {
          return prev.includes(participantGuid) ? prev : [...prev, participantGuid];
        }

        return prev.filter((p) => p !== participantGuid);
      });
      return;
    }

    setIncludedAnnouncementParticipants((prev) => {
      if (currentlySelected) {
        return prev.filter((p) => p !== participantGuid);
      }

      return prev.includes(participantGuid) ? prev : [...prev, participantGuid];
    });
  };

  const handleQueuePlatformAnnouncement = async (e: React.FormEvent) => {
    e.preventDefault();
    clearMessages();

    if (!announcementForm.title.trim() || !announcementForm.message.trim()) {
      setError("Announcement title and message are required.");
      return;
    }

    if (selectedAnnouncementGroups.length === 0 && includedAnnouncementParticipants.length === 0) {
      setError("Select at least one group or participant target.");
      return;
    }

    setLoading(true);
    try {
      const result = await queueOobPlatformAnnouncement({
        title: announcementForm.title.trim(),
        message: announcementForm.message.trim(),
        groupGuids: selectedAnnouncementGroups,
        includeParticipantGuids: includedAnnouncementParticipants,
        excludeParticipantGuids: excludedAnnouncementParticipants,
      });

      setSuccess(`Platform announcement queued for ${result.queuedCount}/${result.recipientCount} recipients.`);
      setAnnouncementForm({ title: "", message: "" });
      setSelectedAnnouncementGroups([]);
      setIncludedAnnouncementParticipants([]);
      setExcludedAnnouncementParticipants([]);
      setShowAnnouncementModal(false);
      await refreshAnnouncements();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Failed to queue platform announcement.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAnnouncementModal = () => {
    clearMessages();
    setAnnouncementForm({ title: "", message: "" });
    setSelectedAnnouncementGroups([]);
    setIncludedAnnouncementParticipants([]);
    setExcludedAnnouncementParticipants([]);
    setShowAnnouncementModal(true);
  };

  const handlePostUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEventGuid) {
      setError("Select an event first.");
      return;
    }

    clearMessages();
    setLoading(true);
    try {
      await postOobUpdate(selectedEventGuid, updateForm);
      setSuccess("Update posted.");
      setUpdateForm(defaultUpdate);
      setShowPostUpdateModal(false);
      await refreshSelectedEvent(selectedEventGuid);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Failed to post update.");
    } finally {
      setLoading(false);
    }
  };

  const handlePin = async (updateGuid: string, isPinned: boolean) => {
    clearMessages();
    setLoading(true);
    try {
      await pinOobUpdate(updateGuid, !isPinned);
      setSuccess(!isPinned ? "Update pinned." : "Update unpinned.");
      if (selectedEventGuid) await refreshSelectedEvent(selectedEventGuid);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Failed to change pin state.");
    } finally {
      setLoading(false);
    }
  };

  const performRetract = async (updateGuid: string) => {
    clearMessages();
    setLoading(true);
    try {
      await retractOobUpdate(updateGuid);
      setSuccess("Update retracted.");
      if (selectedEventGuid) await refreshSelectedEvent(selectedEventGuid);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Failed to retract update.");
    } finally {
      setLoading(false);
    }
  };

  const handleRetract = (updateGuid: string) => {
    setConfirmAction({ type: "retractUpdate", updateGuid });
  };

  const handleUnretract = (updateGuid: string) => {
    setConfirmAction({ type: "unretractUpdate", updateGuid });
  };

  const handleArchiveUpdate = (updateGuid: string) => {
    setConfirmAction({ type: "archiveUpdate", updateGuid });
  };

  const handleOpenEditUpdateModal = (update: OobStatusUpdateDto) => {
    if (update.retractedDt || update.effEndDt) {
      setError("Only published updates can be edited. Un-retract first if needed.");
      return;
    }

    setEditUpdateState({
      updateGuid: update.updateGuid,
      messageTitle: update.messageTitle,
      messageBody: update.messageBody,
      importance: (update.importance as "Normal" | "Important" | "Critical") ?? "Normal",
    });
  };

  const handleSaveEditedUpdate = async () => {
    if (!editUpdateState) {
      return;
    }

    const payload: OobEditUpdateRequest = {
      messageTitle: editUpdateState.messageTitle,
      messageBody: editUpdateState.messageBody,
      importance: editUpdateState.importance,
    };

    clearMessages();
    setLoading(true);
    try {
      await editOobUpdate(editUpdateState.updateGuid, payload);
      setSuccess("Update edited.");
      setEditUpdateState(null);
      if (selectedEventGuid) await refreshSelectedEvent(selectedEventGuid);
    } catch (err: any) {
      setError(getApiErrorMessage(err, "Failed to edit update."));
    } finally {
      setLoading(false);
    }
  };

  const performUnretract = async (updateGuid: string) => {
    clearMessages();
    setLoading(true);
    try {
      await unretractOobUpdate(updateGuid);
      setSuccess("Update restored to published state.");
      if (selectedEventGuid) await refreshSelectedEvent(selectedEventGuid);
    } catch (err: any) {
      setError(getApiErrorMessage(err, "Failed to restore update."));
    } finally {
      setLoading(false);
    }
  };

  const performArchiveUpdate = async (updateGuid: string) => {
    clearMessages();
    setLoading(true);
    try {
      await archiveOobUpdate(updateGuid);
      setSuccess("Update archived.");
      if (selectedEventGuid) await refreshSelectedEvent(selectedEventGuid);
    } catch (err: any) {
      setError(getApiErrorMessage(err, "Failed to archive update."));
    } finally {
      setLoading(false);
    }
  };

  const handleAddParticipantToEvent = async () => {
    if (!selectedParticipantToAdd || !selectedEventGuid) return;
    clearMessages();
    setLoading(true);
    try {
      await addOobEventParticipant(selectedEventGuid, selectedParticipantToAdd);
      setSuccess("Participant added to event.");
      setSelectedParticipantToAdd("");
      setShowAddEventParticipantModal(false);
      await refreshSelectedEvent(selectedEventGuid);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Failed to add participant to event.");
    } finally {
      setLoading(false);
    }
  };

  const performRemoveParticipantFromEvent = async (participantGuid: string) => {
    if (!selectedEventGuid) return;
    clearMessages();
    setLoading(true);
    try {
      await removeOobEventParticipant(selectedEventGuid, participantGuid);
      setSuccess("Participant removed from event.");
      await refreshSelectedEvent(selectedEventGuid);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Failed to remove participant from event.");
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveParticipantFromEvent = (participantGuid: string) => {
    if (!selectedEventGuid) return;
    setConfirmAction({ type: "removeEventParticipant", participantGuid });
  };

  const handleConfirmAction = async () => {
    if (!confirmAction) return;

    const action = confirmAction;
    setConfirmAction(null);

    if (action.type === "retractUpdate") {
      await performRetract(action.updateGuid);
      return;
    }

    if (action.type === "unretractUpdate") {
      await performUnretract(action.updateGuid);
      return;
    }

    if (action.type === "archiveUpdate") {
      await performArchiveUpdate(action.updateGuid);
      return;
    }

    if (action.type === "removeEventParticipant") {
      await performRemoveParticipantFromEvent(action.participantGuid);
    }
  };

  const confirmModal = useMemo(() => {
    if (!confirmAction) {
      return {
        title: "",
        body: "",
        confirmLabel: "Confirm",
        variant: "danger" as "danger" | "warning",
      };
    }

    if (confirmAction.type === "retractUpdate") {
      return {
        title: "Retract Update",
        body: "Retract this update from participants?",
        confirmLabel: "Retract",
        variant: "warning" as "danger" | "warning",
      };
    }

    if (confirmAction.type === "unretractUpdate") {
      return {
        title: "Restore Update",
        body: "Restore this retracted update so participants can see it again?",
        confirmLabel: "Restore",
        variant: "warning" as "danger" | "warning",
      };
    }

    if (confirmAction.type === "archiveUpdate") {
      return {
        title: "Archive Update",
        body: "Archive this update? Archived updates are removed from normal views.",
        confirmLabel: "Archive",
        variant: "danger" as "danger" | "warning",
      };
    }

    return {
      title: "Remove Event Participant",
      body: "Remove this participant from the event?",
      confirmLabel: "Remove",
      variant: "danger" as "danger" | "warning",
    };
  }, [confirmAction]);
  useEffect(() => {
    if (selectedEvent?.deliveryStatus === "TeamsReady" && (eventSubTab === "channels" || eventSubTab === "questions")) {
      setEventSubTab("updates");
    }
  }, [selectedEvent?.deliveryStatus, eventSubTab]);

  const closeActiveEventTab = () => {
    setSelectedEventGuid("");
    setSelectedEvent(null);
    setSecondaryProvisioningStatus(null);
    setEventSubTab("updates");
    setTab("manageEvents");
  };

  const renderActiveEventPanel = () => {
    if (!selectedEvent) {
      return <Alert variant="info">Select an event to view details...</Alert>;
    }

    const isLocalOnlyEvent = selectedEvent.deliveryStatus === "LocalActive";
    const isTeamsReadyEvent = selectedEvent.deliveryStatus === "TeamsReady";

    return (
      <>
        <OobDeliveryStatusBanner event={selectedEvent} />
        <OobEventWorkspaceHeader
          event={selectedEvent}
          participantCount={eventParticipants.length}
          actions={<>
            <Button size="sm" variant="outline-primary" onClick={() => setEventSubTab("updates")}>
              <FaBell className="me-1" aria-hidden="true" />Send Update
            </Button>
            <Dropdown>
              <Dropdown.Toggle size="sm" variant="outline-secondary">
                <FaCog className="me-1" aria-hidden="true" />Event Actions
              </Dropdown.Toggle>
              <Dropdown.Menu align="end">
                <Dropdown.Item as="a" href={publicEventPath(selectedEvent.eventGuid)} target="_blank" rel="noopener noreferrer"><FaLink className="me-2" />View Public Page</Dropdown.Item>
                {selectedEvent.status === "Active" && <Dropdown.Item onClick={handleResendPending}><FaBell className="me-2" />Resend Pending Reminders</Dropdown.Item>}
                {selectedEvent.status !== "Closed" && <Dropdown.Item className="text-danger" onClick={handleCloseEvent}><FaArchive className="me-2" />Stop Event</Dropdown.Item>}
              </Dropdown.Menu>
            </Dropdown>
            {selectedEvent.status === "Active" && <Button size="sm" variant="primary" onClick={handleResolveEvent}><FaHistory className="me-1" />Resolve Event</Button>}
          </>}
        />
        {selectedEvent.isSilent && (
          <div className="oob-event-notice">
            <strong>SILENT MODE ACTIVE</strong> <span className="ms-1">No participant notifications are being sent. Copies go to the event creator only.</span>
          </div>
        )}
        {selectedEvent.eventType === "Onboarding" && (
          <div className="oob-event-notice">
            <strong>ONBOARDING EVENT</strong> <span className="ms-1">Use this event to confirm backup identity and basic OOB access.</span>
          </div>
        )}
        <OobEventMetricStrip compact metrics={[
          { label: "Event Updates", value: selectedEvent.updates.length, icon: FaComments, action: <OverlayTrigger placement="top" overlay={<Tooltip>Open Updates</Tooltip>}><Button variant="link" size="sm" className="oob-event-metric-link" aria-label="Open Updates" onClick={() => setEventSubTab("updates")}><FaExternalLinkAlt /></Button></OverlayTrigger> },
          { label: "Participants", value: eventParticipants.length, icon: FaUsers, action: <OverlayTrigger placement="top" overlay={<Tooltip>Open Participants</Tooltip>}><Button variant="link" size="sm" className="oob-event-metric-link" aria-label="Open Participants" onClick={() => setEventSubTab("eventParticipants")}><FaExternalLinkAlt /></Button></OverlayTrigger> },
          { label: "Acknowledged", value: acknowledgedParticipantGuids.size, icon: FaClipboardList, action: <OverlayTrigger placement="top" overlay={<Tooltip>Open Acknowledgements</Tooltip>}><Button variant="link" size="sm" className="oob-event-metric-link" aria-label="Open Acknowledgements" onClick={() => setEventSubTab("acks")}><FaExternalLinkAlt /></Button></OverlayTrigger> },
          ...(!isTeamsReadyEvent ? [{ label: "Open Questions", value: eventQuestions.filter((question) => !question.isPublished).length, icon: FaQuestionCircle, action: <OverlayTrigger placement="top" overlay={<Tooltip>Open Questions</Tooltip>}><Button variant="link" size="sm" className="oob-event-metric-link" aria-label="Open Questions" onClick={() => setEventSubTab("questions")}><FaExternalLinkAlt /></Button></OverlayTrigger> }] : []),
          { label: "Event Files", value: eventArtifacts.length, icon: FaFileAlt, action: <OverlayTrigger placement="top" overlay={<Tooltip>Open Event Files</Tooltip>}><Button variant="link" size="sm" className="oob-event-metric-link" aria-label="Open Event Files" onClick={() => setEventSubTab("files")}><FaExternalLinkAlt /></Button></OverlayTrigger> },
          { label: "Event Duration", value: formatEventDuration(selectedEvent.activatedDt || selectedEvent.createDt), icon: FaClock, action: <OverlayTrigger placement="top" overlay={<Tooltip>Open Event Log</Tooltip>}><Button variant="link" size="sm" className="oob-event-metric-link" aria-label="Open Event Log" onClick={() => setEventSubTab("audit")}><FaExternalLinkAlt /></Button></OverlayTrigger> },
        ]} />
        <div className={`oob-event-detail-layout ${eventSidebarCollapsed ? "oob-event-detail-layout--sidebar-collapsed" : ""}`}>
        <Row className="g-3 mb-3">
          <Col lg={8}>
            <Card className="h-100">
              <Card.Header className="d-flex align-items-center justify-content-between gap-2"><span>Event Information</span><Button size="sm" variant="link" className="p-0" aria-label={eventSidebarCollapsed ? "Expand event information rail" : "Collapse event information rail"} onClick={() => setEventSidebarCollapsed((collapsed) => !collapsed)}>{eventSidebarCollapsed ? <FaAngleLeft /> : <FaAngleRight />}</Button></Card.Header>
              {!eventSidebarCollapsed && <Card.Body className="oob-event-information">
                <dl className="oob-event-information__list">
                  <div><dt>Event ID</dt><dd>{selectedEvent.eventGuid}</dd></div>
                  <div><dt>Delivery</dt><dd>{selectedEvent.deliveryStatus}</dd></div>
                  <div><dt>Created</dt><dd>{fmt(selectedEvent.createDt)}</dd></div>
                  <div><dt>Created by</dt><dd>{selectedEvent.createdBy}</dd></div>
                </dl>
                <a className="oob-event-quick-link text-decoration-none mt-2" href={participantPublicPath(selectedEvent.eventGuid)} target="_blank" rel="noopener noreferrer">
                  <span><FaLink className="me-2" />Participant Event Page</span><span>›</span>
                </a>
              </Card.Body>}
            </Card>
          </Col>
          <Col lg={4} className={`oob-event-sidebar ${eventSidebarCollapsed ? "is-collapsed" : ""}`}>
            {!eventSidebarCollapsed && <>
            {secondaryProvisioningStatus && (
              <Card className="oob-event-support-card mb-3">
                <Card.Header className="d-flex justify-content-between align-items-center gap-2">
                  <span>Secondary Tenant</span>
                  <div className="d-flex align-items-center gap-2">
                    <Badge bg={secondaryProvisioningStatus.status === "Provisioned" ? "success" : "warning"} text={secondaryProvisioningStatus.status === "Provisioned" ? undefined : "dark"}>
                      {secondaryProvisioningStatus.status}
                    </Badge>
                    {secondaryProvisioningStatus.status !== "Provisioned" && (
                      <Button size="sm" variant="outline-primary" onClick={handleRetrySecondaryProvisioning} disabled={secondaryProvisioningLoading}>
                        {secondaryProvisioningLoading ? "Retrying..." : "Retry"}
                      </Button>
                    )}
                  </div>
                </Card.Header>
                <Card.Body>
                  {secondaryProvisioningStatus.error && (
                    <Alert variant="danger" className="py-2">
                      <strong>Provisioning error:</strong> {secondaryProvisioningStatus.error}
                    </Alert>
                  )}
                  <dl className="oob-event-information__list small">
                    <div><dt>Team</dt><dd>{secondaryProvisioningStatus.teamDisplayName || secondaryProvisioningStatus.teamId || "Not created"}</dd></div>
                    <div><dt>Participants</dt><dd>{secondaryProvisioningStatus.participantProvisionedCount} provisioned / {secondaryProvisioningStatus.participantFailureCount} failed</dd></div>
                    <div><dt>Files</dt><dd>{secondaryProvisioningStatus.artifactProvisionedCount} copied / {secondaryProvisioningStatus.artifactFailureCount} failed</dd></div>
                    <div><dt>Updates</dt><dd>{secondaryProvisioningStatus.messagePublishedCount} published / {secondaryProvisioningStatus.messageFailureCount} failed</dd></div>
                    <div><dt>Last attempt</dt><dd>{fmt(secondaryProvisioningStatus.lastAttemptDt)}</dd></div>
                    <div><dt>Provisioned</dt><dd>{fmt(secondaryProvisioningStatus.provisionedDt)}</dd></div>
                  </dl>
                </Card.Body>
              </Card>
            )}
            </>}
          </Col>
        </Row>
        {eventSidebarCollapsed && (
          <div className="oob-event-sidebar-rail" aria-label="Expand event information">
            <button type="button" title="Expand Event Information" onClick={() => setEventSidebarCollapsed(false)}>Event Information</button>
          </div>
        )}

        <div className="oob-event-main-panel">
        <Tab.Container activeKey={eventSubTab} onSelect={(key) => setEventSubTab(key || "updates")}>
          <Row className="g-3 oob-event-workspace-row">
            <Col lg="auto">
              <div className={`oob-event-subnav ${eventNavigationCollapsed ? "is-collapsed" : ""}`}>
                <div className="oob-event-subnav__header">
                  {!eventNavigationCollapsed && <span>Event Sections</span>}
                  <OverlayTrigger placement="right" overlay={<Tooltip>{eventNavigationCollapsed ? "Expand event navigation" : "Collapse event navigation"}</Tooltip>}>
                    <Button size="sm" variant="link" className="oob-event-subnav__toggle" aria-label={eventNavigationCollapsed ? "Expand event navigation" : "Collapse event navigation"} onClick={() => setEventNavigationCollapsed((collapsed) => !collapsed)}>
                      {eventNavigationCollapsed ? <FaAngleRight /> : <FaAngleLeft />}
                    </Button>
                  </OverlayTrigger>
                </div>
                <Nav variant="pills" className="flex-column" aria-label="Event sections">
                  <Nav.Item><Nav.Link eventKey="updates" title="Updates"><FaBell /><span>Updates</span></Nav.Link></Nav.Item>
                  {!isTeamsReadyEvent && <Nav.Item><Nav.Link eventKey="channels" title="Channels"><FaComments /><span>Channels</span></Nav.Link></Nav.Item>}
                  <Nav.Item><Nav.Link eventKey="eventParticipants" title="Participants"><FaUsers /><span>Participants</span></Nav.Link></Nav.Item>
                  {!isTeamsReadyEvent && <Nav.Item><Nav.Link eventKey="questions" title="Questions"><FaQuestionCircle /><span>Questions ({eventQuestions.filter((question) => !question.isPublished).length})</span></Nav.Link></Nav.Item>}
                  <Nav.Item><Nav.Link eventKey="acks" title="Acknowledgements"><FaClipboardList /><span>Acknowledgements</span></Nav.Link></Nav.Item>
                  <Nav.Item><Nav.Link eventKey="files" title="Event Files"><FaFileAlt /><span>Event Files</span></Nav.Link></Nav.Item>
                  <Nav.Item><Nav.Link eventKey="closeout" title="Event Closeout"><FaArchive /><span>Event Closeout</span></Nav.Link></Nav.Item>
                  <Nav.Item><Nav.Link eventKey="audit" title="Event Log"><FaHistory /><span>Event Log</span></Nav.Link></Nav.Item>
                </Nav>
              </div>
            </Col>
            <Col>
              <div className="oob-event-content-panel">
              <Tab.Content>
          <Tab.Pane eventKey="updates">
            <Row className="g-3 mt-1">
              <Col>
                <Card>
                  <Card.Header className="d-flex justify-content-between align-items-center gap-2 flex-wrap">
                    <span>Updates ({filteredUpdates.length})</span>
                    <Button size="sm" variant="outline-primary" onClick={() => setShowPostUpdateModal(true)}><FaBell className="me-1" />New Update</Button>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <div className="d-flex justify-content-between align-items-center gap-2 p-3 border-bottom flex-wrap">
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <Badge bg="success">Published: {lifecycleCounts.published}</Badge>
                        <Badge bg="warning" text="dark">Retracted: {lifecycleCounts.retracted}</Badge>
                        <Badge bg="secondary">Archived: {lifecycleCounts.archived}</Badge>
                      </div>
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                      <Form.Label className="mb-0 small text-muted">Lifecycle</Form.Label>
                      <Form.Select
                        size="sm"
                        value={updateLifecycleFilter}
                        onChange={(e) => setUpdateLifecycleFilter(e.target.value as UpdateLifecycleFilter)}
                        style={{ minWidth: 170 }}
                      >
                        <option value="Active">Active (Published + Retracted)</option>
                        <option value="Published">Published</option>
                        <option value="Retracted">Retracted</option>
                        <option value="Archived">Archived</option>
                        <option value="All">All</option>
                      </Form.Select>
                      <Form.Check
                        type="switch"
                        id="show-actionable-only"
                        label="Show actionable only"
                        checked={showActionableOnly}
                        onChange={(e) => setShowActionableOnly(e.target.checked)}
                      />
                    </div>
                    </div>
                    <Table hover responsive className="mb-0">
                      <thead><tr><th>Title</th><th>Importance</th><th>Lifecycle</th><th>Pinned</th><th>Created</th><th>Actions</th></tr></thead>
                      <tbody>
                        {filteredUpdates.map((u) => {
                          const lifecycle = getUpdateLifecycle(u);
                          const isArchived = lifecycle === "Archived";
                          const isRetracted = lifecycle === "Retracted";
                          const isPublished = lifecycle === "Published";
                          const isPinnedPublished = isPublished && u.isPinned;

                          const rowClassName = isArchived
                            ? "table-secondary"
                            : isRetracted
                              ? "table-warning"
                              : isPinnedPublished
                                ? "oob-update-row-pinned"
                                : undefined;

                          return (
                          <tr
                            key={u.updateGuid}
                            className={rowClassName}
                            title={isPinnedPublished ? "Pinned update" : undefined}
                          >
                            <td>
                              <div className="fw-semibold">{u.messageTitle}</div>
                              <div className="small text-muted">{u.messageBody}</div>
                              {u.linkUrl && (
                                <div className="small">
                                  <a href={u.linkUrl} target="_blank" rel="noopener noreferrer">
                                    {u.linkLabel || u.linkUrl}
                                  </a>
                                </div>
                              )}
                            </td>
                            <td><Badge bg={u.importance === "Critical" ? "danger" : u.importance === "Important" ? "warning" : "secondary"}>{u.importance}</Badge></td>
                            <td>
                              {isArchived ? <Badge bg="secondary">Archived</Badge> : isRetracted ? <Badge bg="warning" text="dark">Retracted</Badge> : <Badge bg="success">Published</Badge>}
                            </td>
                            <td>{isPublished && u.isPinned ? "Yes" : "No"}</td>
                            <td>{fmt(u.createDt)}</td>
                            <td>
                              <div className="d-flex flex-wrap gap-1">
                                {isPublished && (
                                  <>
                                    <OverlayTrigger placement="top" overlay={<Tooltip>{u.isPinned ? "Unpin update" : "Pin update"}</Tooltip>}>
                                      <Button
                                        size="sm"
                                        variant={u.isPinned ? "success" : "outline-secondary"}
                                        aria-label={u.isPinned ? "Unpin update" : "Pin update"}
                                        aria-pressed={u.isPinned}
                                        onClick={() => handlePin(u.updateGuid, u.isPinned)}
                                      >
                                        <FaThumbtack className={u.isPinned ? "" : "opacity-50"} />
                                      </Button>
                                    </OverlayTrigger>
                                    <OverlayTrigger placement="top" overlay={<Tooltip>Edit update</Tooltip>}>
                                      <Button
                                        size="sm"
                                        variant="outline-secondary"
                                        aria-label="Edit update"
                                        onClick={() => handleOpenEditUpdateModal(u)}
                                      >
                                        <FaEdit />
                                      </Button>
                                    </OverlayTrigger>
                                    <OverlayTrigger placement="top" overlay={<Tooltip>Retract update</Tooltip>}>
                                      <Button
                                        size="sm"
                                        variant="outline-warning"
                                        aria-label="Retract update"
                                        onClick={() => handleRetract(u.updateGuid)}
                                      >
                                        <FaEyeSlash />
                                      </Button>
                                    </OverlayTrigger>
                                    <OverlayTrigger placement="top" overlay={<Tooltip>Archive update</Tooltip>}>
                                      <Button
                                        size="sm"
                                        variant="outline-danger"
                                        aria-label="Archive update"
                                        onClick={() => handleArchiveUpdate(u.updateGuid)}
                                      >
                                        <FaArchive />
                                      </Button>
                                    </OverlayTrigger>
                                  </>
                                )}
                                {isRetracted && (
                                  <>
                                    <OverlayTrigger placement="top" overlay={<Tooltip>Restore update</Tooltip>}>
                                      <Button
                                        size="sm"
                                        variant="outline-success"
                                        aria-label="Restore update"
                                        onClick={() => handleUnretract(u.updateGuid)}
                                      >
                                        <FaUndo />
                                      </Button>
                                    </OverlayTrigger>
                                    <OverlayTrigger placement="top" overlay={<Tooltip>Archive update</Tooltip>}>
                                      <Button
                                        size="sm"
                                        variant="outline-danger"
                                        aria-label="Archive update"
                                        onClick={() => handleArchiveUpdate(u.updateGuid)}
                                      >
                                        <FaArchive />
                                      </Button>
                                    </OverlayTrigger>
                                  </>
                                )}
                                {isArchived && <span className="small text-muted">No actions</span>}
                              </div>
                            </td>
                          </tr>
                        )})}
                        {filteredUpdates.length === 0 && (<tr><td colSpan={6} className="text-center text-muted py-3">No updates in this lifecycle filter.</td></tr>)}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab.Pane>

          <Tab.Pane eventKey="channels">
            <Row className="g-3 mt-1 oob-local-channel-workspace">
              <Col lg={4}>
                <Card className="h-100">
                  <Card.Header>Local Channels</Card.Header>
                  <Card.Body className="p-2">
                    {localEventChannels.length === 0 ? <div className="small text-muted p-2">Channels are created when this event activates its local communication space.</div> : (
                      <Nav className="flex-column gap-1">
                        {localEventChannels.map((channel) => (
                          <Nav.Link key={channel.channelGuid} active={selectedLocalChannelGuid === channel.channelGuid} onClick={() => void handleSelectLocalChannel(channel.channelGuid)} className="oob-local-channel-link">
                            <div className="d-flex justify-content-between gap-2"><span className="fw-semibold">{channel.displayName}</span><span className="small">{channel.messageCount}</span></div>
                            <div className="small text-muted">{channel.description}</div>
                          </Nav.Link>
                        ))}
                      </Nav>
                    )}
                  </Card.Body>
                </Card>
              </Col>
              <Col lg={8}>
                {(() => {
                  const selectedChannel = localEventChannels.find((channel) => channel.channelGuid === selectedLocalChannelGuid);
                  return <Card className="h-100">
                    <Card.Header>{selectedChannel ? <><span>{selectedChannel.displayName}</span><span className="small text-muted ms-2">{selectedChannel.audienceScope === "Admins" ? "OOB admins only" : "All event participants"}</span></> : "Select a channel"}</Card.Header>
                    <Card.Body className="oob-local-channel-messages">
                      {selectedChannel ? (
                        <>
                          {localChannelMessages.length === 0 ? <div className="text-muted text-center py-4">No messages yet.</div> : localChannelMessages.map((message) => (
                            <div key={message.messageGuid} className="oob-local-channel-message">
                              <div className="d-flex justify-content-between gap-2"><span className="fw-semibold">{message.authorDisplayName || message.authorEmail}</span><span className="small text-muted">{fmt(message.createDt)}</span></div>
                              <div className="mt-1">{message.messageBody}</div>
                            </div>
                          ))}
                          <Form className="mt-3" onSubmit={handlePostLocalChannelMessage}>
                            <Form.Group><Form.Label className="visually-hidden">New channel message</Form.Label><Form.Control as="textarea" rows={3} placeholder={`Post to ${selectedChannel.displayName}`} value={localChannelMessageBody} onChange={(event) => setLocalChannelMessageBody(event.target.value)} maxLength={4000} /></Form.Group>
                            <div className="d-flex justify-content-end mt-2"><Button size="sm" type="submit" disabled={postingLocalChannelMessage || !localChannelMessageBody.trim()}>{postingLocalChannelMessage ? "Posting..." : "Post Message"}</Button></div>
                          </Form>
                        </>
                      ) : <div className="text-muted text-center py-4">Select a local channel to view its messages.</div>}
                    </Card.Body>
                  </Card>;
                })()}
              </Col>
            </Row>
          </Tab.Pane>

          <Tab.Pane eventKey="eventParticipants">
            <Card className="mt-1">
                  <Card.Header className="d-flex align-items-center justify-content-between gap-2">
                    <span>Event Participants ({eventParticipants.length})</span>
                    <Button size="sm" variant="outline-primary" onClick={() => setShowAddEventParticipantModal(true)}><FaUsers className="me-1" />Add Participant</Button>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <Table hover responsive className="mb-0">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Status</th>
                          <th>Added</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {eventParticipants.map((ep) => {
                          const participantMeta = participantMetaByGuid.get(ep.participantGuid);
                          const isActive = participantMeta?.isActive ?? true;
                          const isConfirmed = acknowledgedParticipantGuids.has(ep.participantGuid);

                          return (
                          <tr key={ep.participantGuid} className={!isActive ? "table-secondary" : undefined}>
                            <td>{ep.displayName}</td>
                            <td>{ep.email}</td>
                            <td>
                              <div className="d-flex gap-1 flex-wrap">
                                <span className={`oob-event-status-chip oob-event-status-chip--${isActive ? "active" : "inactive"}`}>{isActive ? "Active" : "Inactive"}</span>
                                <span className={`oob-event-status-chip oob-event-status-chip--${isConfirmed ? "confirmed" : "pending"}`}>{isConfirmed ? "Confirmed" : "Pending Confirmation"}</span>
                              </div>
                            </td>
                            <td>{fmt(ep.createDt)}</td>
                            <td>
                              <div className="d-flex flex-wrap gap-1">
                                <OverlayTrigger placement="top" overlay={<Tooltip>Copy participant link</Tooltip>}>
                                  <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    aria-label="Copy participant link"
                                    onClick={() => handleCopyParticipantLink(selectedEvent.eventGuid)}
                                  >
                                    <FaLink />
                                  </Button>
                                </OverlayTrigger>
                                <OverlayTrigger placement="top" overlay={<Tooltip>Remove participant</Tooltip>}>
                                  <Button
                                    size="sm"
                                    variant="outline-danger"
                                    aria-label="Remove participant"
                                    onClick={() => handleRemoveParticipantFromEvent(ep.participantGuid)}
                                  >
                                    <FaUserMinus />
                                  </Button>
                                </OverlayTrigger>
                              </div>
                            </td>
                          </tr>
                        )})}
                        {eventParticipants.length === 0 && (
                          <tr><td colSpan={5} className="text-center text-muted py-3">No participants assigned to this event.</td></tr>
                        )}
                      </tbody>
                    </Table>
                  </Card.Body>
            </Card>
          </Tab.Pane>

          <Tab.Pane eventKey="questions">
            <Card className="mt-1">
              <Card.Header>Participant Questions</Card.Header>
              <Card.Body>
                {eventQuestions.length === 0 ? (
                  <div className="text-muted">No participant questions have been submitted.</div>
                ) : (
                  <div className="d-flex flex-column gap-3">
                    {eventQuestions.map((question) => (
                      <div key={question.questionGuid} className="border rounded p-3">
                        <div className="d-flex justify-content-between gap-2 flex-wrap">
                          <div className="fw-semibold">{question.questionText}</div>
                          <div className="d-flex align-items-center gap-2">
                            <span className={`oob-event-status-chip oob-event-status-chip--${question.isPublished ? "confirmed" : "pending"}`}>{question.isPublished ? "Published" : "Awaiting review"}</span>
                            <OverlayTrigger placement="top" overlay={<Tooltip>Edit question</Tooltip>}><Button size="sm" variant="outline-secondary" aria-label="Edit question" onClick={() => setEditEventQuestion({ questionGuid: question.questionGuid, questionText: question.questionText, answerText: question.answerText || "", displayAsAnonymous: question.displayAsAnonymous, importance: question.importance as OobEditEventQuestionRequest["importance"] })}><FaEdit /></Button></OverlayTrigger>
                          </div>
                        </div>
                        {question.isPublished ? (
                          <div className="small text-muted mt-2">Published {fmt(question.publishedDt)}</div>
                        ) : (
                          <>
                            <Form.Control className="mt-3" as="textarea" rows={3} placeholder="Answer for participants" value={questionAnswer[question.questionGuid] || ""} onChange={(event) => setQuestionAnswer((current) => ({ ...current, [question.questionGuid]: event.target.value }))} />
                            <Button className="mt-2" size="sm" variant="primary" disabled={publishingQuestionGuid === question.questionGuid || !questionAnswer[question.questionGuid]?.trim()} onClick={() => handlePublishQuestion(question)}>
                              {publishingQuestionGuid === question.questionGuid ? "Publishing..." : "Answer and Publish Q&A"}
                            </Button>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Tab.Pane>

          <Tab.Pane eventKey="acks">
            <Card className="mt-3">
              <Card.Header className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
                <span>Acknowledgement Summary</span>
                <div className="d-flex align-items-center gap-2 small">
                  <span className="oob-event-status-chip oob-event-status-chip--confirmed">{acknowledgedParticipantGuids.size} confirmed</span>
                  <span className="oob-event-status-chip oob-event-status-chip--pending">{Math.max(0, eventParticipants.length - acknowledgedParticipantGuids.size)} pending</span>
                </div>
              </Card.Header>
              <Card.Body className="oob-event-ack-progress">
                <div className="d-flex justify-content-between small mb-1"><span>Response progress</span><strong>{acknowledgementPercent}%</strong></div>
                <div className="oob-event-ack-progress__track" role="progressbar" aria-label="Participant acknowledgement progress" aria-valuenow={acknowledgementPercent} aria-valuemin={0} aria-valuemax={100}>
                  <div className="oob-event-ack-progress__value" style={{ width: `${acknowledgementPercent}%` }} />
                </div>
              </Card.Body>
              <Card.Body className="p-0">
                <Table hover responsive className="mb-0">
                  <thead><tr><th>Participant</th><th>Email</th><th>Status</th><th>Last Acknowledgement</th></tr></thead>
                  <tbody>
                    {acks.map((a) => (
                      <tr key={a.participantGuid}>
                        <td>{a.displayName}</td>
                        <td>{a.email}</td>
                        <td><span className={`oob-event-status-chip oob-event-status-chip--${a.acknowledgementCount > 0 ? "confirmed" : "pending"}`}>{a.acknowledgementCount > 0 ? "Confirmed" : "Pending"}</span></td>
                        <td>{fmt(a.lastAcknowledgedDt)}</td>
                      </tr>
                    ))}
                    {acks.length === 0 && (<tr><td colSpan={4} className="text-center text-muted py-3">No acknowledgements yet.</td></tr>)}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Tab.Pane>

          <Tab.Pane eventKey="files">
            <Card className="mt-1 oob-event-files">
              <Card.Header className="d-flex align-items-center justify-content-between gap-2">
                <span><FaFileAlt className="me-2" />Event Files</span>
                <span className="small text-muted">{eventArtifacts.length} files</span>
              </Card.Header>
              <Card.Body className="p-0">
                <div className="oob-event-files__folders">
                  <button type="button" className={eventFileFolder === "All Files" ? "active" : ""} onClick={() => setEventFileFolder("All Files")}><FaFolder />All Files <span>{eventArtifacts.length}</span></button>
                  {eventFileFolders.map((folder) => (
                    <button type="button" key={folder} className={eventFileFolder === folder ? "active" : ""} onClick={() => setEventFileFolder(folder)}><FaFolder />{folder} <span>{eventArtifacts.filter((artifact) => (artifact.artifactType || "Other") === folder).length}</span></button>
                  ))}
                </div>
                <div className="oob-event-files__breadcrumb">Event Files <span>/</span> {eventFileFolder}</div>
                <Table hover responsive className="mb-0">
                  <thead><tr><th>Name</th><th>Modified</th><th>Size</th><th>Modified By</th><th>Actions</th></tr></thead>
                  <tbody>
                    {visibleEventArtifacts.map((artifact) => (
                      <tr key={artifact.artifactGuid}>
                        <td><div className="d-flex align-items-center gap-2"><FaFileAlt className="oob-event-files__file-icon" /><div><div className="fw-semibold">{artifact.artifactName}</div><div className="small text-muted">{artifact.artifactType} · {artifact.provisioningStatus}</div></div></div></td>
                        <td className="small">{fmt(artifact.lastUpdateDt || artifact.provisionedDt || artifact.createDt)}</td>
                        <td className="small">{fmtBytes(artifact.fileSizeBytes)}</td>
                        <td className="small">{selectedEvent.createdBy}</td>
                        <td><Button size="sm" variant="outline-secondary" onClick={() => handleDownloadEventArtifact(artifact, selectedEvent.eventGuid)}>Download</Button></td>
                      </tr>
                    ))}
                    {visibleEventArtifacts.length === 0 && <tr><td colSpan={5} className="text-center text-muted py-3">No files are available in this folder.</td></tr>}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Tab.Pane>

          <Tab.Pane eventKey="closeout">
            {selectedEvent.status === "Active" || selectedEvent.status === "Draft" ? (
              <Card className="mt-1"><Card.Header>Event Closeout</Card.Header><Card.Body className="text-muted">Resolve the active event to begin reconciliation and complete its controlled closeout.</Card.Body></Card>
            ) : selectedEvent.status === "Closed" ? (
              <Card className="mt-1"><Card.Header>Event Closeout</Card.Header><Card.Body className="text-muted">This event was stopped directly and has no controlled reconciliation workflow.</Card.Body></Card>
            ) : (
              <Card className="oob-event-support-card mt-1">
                <Card.Header>Event Lifecycle (Reconciliation &amp; Deprovisioning)</Card.Header>
                <Card.Body>
                  {isLocalOnlyEvent && (
                    <div className="small text-muted mb-2">
                      This event only used the local communication space — no Secondary Tenant Team was created, so there is no external membership to reconcile or Team to delete.
                    </div>
                  )}
                  {!isLocalOnlyEvent && selectedEvent.status === "Resolved" && (
                    <div className="small text-muted mb-2">
                      The Secondary Tenant Team was archived (read-only) on Resolve — all content and membership is preserved for review.
                    </div>
                  )}
                  <div className="d-flex flex-wrap gap-2 mb-3">
                    {selectedEvent.status === "Resolved" && (
                      <>
                        {!isLocalOnlyEvent && !(reconcileCheckedClean && !eventFollowUps.some((f) => f.status === "Open")) && (
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={handleReconcileMembership}
                            disabled={lifecycleActionLoading}
                          >
                            {lifecycleActionLoading ? "Reconciling..." : "Reconcile Membership"}
                          </Button>
                        )}
                        {!isLocalOnlyEvent && reconcileCheckedClean && !eventFollowUps.some((f) => f.status === "Open") && (
                          <span className="small text-success align-self-center">Membership reconciled — no open follow-ups.</span>
                        )}
                        <Button
                          size="sm"
                          variant="outline-success"
                          onClick={handleFinalizeEvent}
                          disabled={lifecycleActionLoading || eventFollowUps.some((f) => f.status === "Open")}
                        >
                          {isLocalOnlyEvent ? "Finalize Event" : "Finalize Event (Remove Participants)"}
                        </Button>
                      </>
                    )}
                    {selectedEvent.status === "Finalized" && (
                      <>
                        <div className="small text-muted mb-2">
                          {isLocalOnlyEvent ? "This local event is finalized. There is no Team to deprovision, but you can mark closeout complete for reporting." : "Event participants have been removed from the Team. Administrators/Support and guest identities are retained."}
                        </div>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={handleDeprovisionEvent}
                          disabled={lifecycleActionLoading}
                        >
                          {isLocalOnlyEvent ? "Mark Closeout Complete" : "Deprovision Event (Delete Team)"}
                        </Button>
                      </>
                    )}
                    {selectedEvent.status === "Deprovisioned" && (
                      <span className="small text-muted">{isLocalOnlyEvent ? "This local event's closeout is complete." : "The Secondary Tenant Team and its channels have been deleted. Guest identities and Administrators/Support group membership are retained for future events."}</span>
                    )}
                  </div>

                  {selectedEvent.status === "Resolved" && eventFollowUps.some((f) => f.status === "Open") && (
                    <Alert variant="warning" className="py-2">
                      Open reconciliation follow-ups must be resolved before this event can be finalized.
                    </Alert>
                  )}

                  <div className="small fw-semibold mb-1">Reconciliation Follow-ups ({eventFollowUps.length})</div>
                  {eventFollowUps.length === 0 ? (
                    <div className="small text-muted">No follow-ups recorded.</div>
                  ) : (
                    <Table size="sm" hover responsive className="mb-0">
                      <thead>
                        <tr>
                          <th>Type</th>
                          <th>Member</th>
                          <th>Description</th>
                          <th>Status</th>
                          <th>Created</th>
                          <th>Resolve</th>
                        </tr>
                      </thead>
                      <tbody>
                        {eventFollowUps.map((f) => (
                          <tr key={f.followUpGuid}>
                            <td>{f.exceptionType}</td>
                            <td>
                              {f.participantEmail || (f.guestObjectId ? <span className="text-muted small">{f.guestObjectId}</span> : "-")}
                            </td>
                            <td>{f.description}</td>
                            <td>
                              <Badge bg={f.status === "Open" ? "warning" : "secondary"} text={f.status === "Open" ? "dark" : undefined}>{f.status}</Badge>
                              {f.status !== "Open" && f.resolutionNote && (
                                <div className="small text-muted mt-1">{f.resolutionNote}</div>
                              )}
                              {f.status !== "Open" && f.resolvedBy && (
                                <div className="small text-muted">by {f.resolvedBy} on {fmt(f.resolvedDt)}</div>
                              )}
                            </td>
                            <td>{fmt(f.createDt)}</td>
                            <td>
                              {f.status === "Open" && (
                                <div className="d-flex flex-column gap-1" style={{ minWidth: 180 }}>
                                  <Form.Control
                                    size="sm"
                                    placeholder="Resolution note (optional)"
                                    value={followUpNotes[f.followUpGuid] || ""}
                                    onChange={(e) => setFollowUpNotes((prev) => ({ ...prev, [f.followUpGuid]: e.target.value }))}
                                  />
                                  <Button
                                    size="sm"
                                    variant="outline-success"
                                    disabled={resolvingFollowUpGuid === f.followUpGuid}
                                    onClick={() => handleResolveFollowUp(f.followUpGuid)}
                                  >
                                    {resolvingFollowUpGuid === f.followUpGuid ? "Resolving..." : "Mark Resolved"}
                                  </Button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Card.Body>
              </Card>
            )}
          </Tab.Pane>

          <Tab.Pane eventKey="audit">
            <Card className="mt-3">
              <Card.Header className="d-flex justify-content-between align-items-center gap-2 flex-wrap">
                <div>Event Log ({sortedAudit.length})</div>
                <div className="d-flex gap-2">
                  <Form.Control
                    size="sm"
                    placeholder="Search message, type, severity, user"
                    value={auditSearch}
                    onChange={(e) => setAuditSearch(e.target.value)}
                    style={{ minWidth: 280 }}
                  />
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => selectedEvent && refreshSelectedEvent(selectedEvent.eventGuid)}
                  >
                    Refresh
                  </Button>
                </div>
              </Card.Header>
              <Card.Body className="border-bottom">
                <Row className="g-2">
                  <Col md={2}>
                    <Form.Select size="sm" value={auditSeverityFilter} onChange={(e) => setAuditSeverityFilter(e.target.value)}>
                      <option value="All">All Severities</option>
                      {auditSeverities.map((severity) => (
                        <option key={severity} value={severity}>{severity}</option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={3}>
                    <Form.Select size="sm" value={auditTypeFilter} onChange={(e) => setAuditTypeFilter(e.target.value)}>
                      <option value="All">All Types</option>
                      {auditTypes.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={3}>
                    <Form.Select size="sm" value={auditUserFilter} onChange={(e) => setAuditUserFilter(e.target.value)}>
                      <option value="All">All Users</option>
                      {auditUsers.map((user) => (
                        <option key={user} value={user}>{user}</option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={2}>
                    <Form.Select size="sm" value={auditTimeFilter} onChange={(e) => setAuditTimeFilter(e.target.value)}>
                      <option value="All">All Time</option>
                      <option value="Last1Hour">Last 1 Hour</option>
                      <option value="Last24Hours">Last 24 Hours</option>
                      <option value="Last7Days">Last 7 Days</option>
                      <option value="Last30Days">Last 30 Days</option>
                    </Form.Select>
                  </Col>
                  <Col md={2} className="d-flex justify-content-end">
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => {
                        setAuditSearch("");
                        setAuditSeverityFilter("All");
                        setAuditTypeFilter("All");
                        setAuditUserFilter("All");
                        setAuditTimeFilter("All");
                        setAuditIssuesOnly(false);
                      }}
                    >
                      Clear Filters
                    </Button>
                  </Col>
                </Row>
                <Row className="g-2 mt-1 align-items-center">
                  <Col md={3}>
                    <Form.Check
                      type="switch"
                      id="audit-issues-only"
                      label="Issues Only (Warning + Error)"
                      checked={auditIssuesOnly}
                      onChange={(e) => setAuditIssuesOnly(e.target.checked)}
                    />
                  </Col>
                  <Col md={9}>
                    {activeAuditFilters.length > 0 && (
                      <div className="d-flex gap-1 flex-wrap justify-content-end">
                        {activeAuditFilters.map((chip) => (
                          <Button
                            key={chip.key}
                            size="sm"
                            variant="dark"
                            className="py-0 px-2"
                            onClick={() => handleRemoveAuditFilter(chip.key)}
                            title="Remove filter"
                          >
                            {chip.label} ×
                          </Button>
                        ))}
                      </div>
                    )}
                  </Col>
                </Row>
              </Card.Body>
              <Card.Body className="p-0">
                <Table hover responsive className="mb-0 oob-event-log-table">
                  <thead><tr>
                    <th><button type="button" className="oob-event-sort" onClick={() => toggleAuditSort("time")}>Time {auditSortColumn === "time" && (auditSortAscending ? "↑" : "↓")}</button></th>
                    <th><button type="button" className="oob-event-sort" onClick={() => toggleAuditSort("type")}>Type {auditSortColumn === "type" && (auditSortAscending ? "↑" : "↓")}</button></th>
                    <th><button type="button" className="oob-event-sort" onClick={() => toggleAuditSort("severity")}>Severity {auditSortColumn === "severity" && (auditSortAscending ? "↑" : "↓")}</button></th>
                    <th><button type="button" className="oob-event-sort" onClick={() => toggleAuditSort("user")}>User {auditSortColumn === "user" && (auditSortAscending ? "↑" : "↓")}</button></th>
                    <th>Message</th>
                  </tr></thead>
                  <tbody>
                    {sortedAudit.map((item) => (
                      <tr key={item.outOfBandAuditEventId}>
                        <td className="oob-event-log-table__meta">{fmt(item.actionTimestamp)}</td>
                        <td className="oob-event-log-table__meta">{item.auditType}</td>
                        <td><span className={`oob-event-status-chip oob-event-status-chip--${item.severity === "Error" ? "error" : item.severity === "Warning" ? "pending" : "info"}`}>{item.severity}</span></td>
                        <td className="oob-event-log-table__meta">{item.actionUser}</td>
                        <td className="oob-event-log-table__message">{item.message}</td>
                      </tr>
                    ))}
                    {sortedAudit.length === 0 && (<tr><td colSpan={5} className="text-center text-muted py-3">No event log entries match the current filters.</td></tr>)}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Tab.Pane>
              </Tab.Content>
              </div>
            </Col>
          </Row>
        </Tab.Container>
        </div>
        </div>
      </>
    );
  };

  return (
    <div className="container-fluid px-4 pt-2 pb-4">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div>
          <h4 className="mb-1">Out-of-Band Communications</h4>
          <div className="text-muted">POC control plane for readiness, status board, and acknowledgements.</div>
        </div>
        <div className="d-flex align-items-center gap-2">
          {adminSessionRemainingMs !== null && (
            <Badge bg={adminSessionRemainingMs <= 10 * 60000 ? "warning" : "secondary"} text={adminSessionRemainingMs <= 10 * 60000 ? "dark" : undefined}>
              Break-glass {fmtRemaining(adminSessionRemainingMs)}
            </Badge>
          )}
          {((loading && !activatingEventGuid) || invitingParticipantGuid) && <Spinner animation="border" size="sm" />}
        </div>
      </div>

      {activatingEventGuid && (
        <Alert variant="info" className="d-flex align-items-center gap-3">
          <Spinner animation="border" size="sm" />
          <span>
            <strong>Activating event…</strong> Provisioning the Secondary Tenant Team, channels, and guest members. This can occasionally take up to 1-2 minutes if Microsoft 365 is slow to propagate the new group.
          </span>
        </Alert>
      )}
      {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess(null)} dismissible>{success}</Alert>}
      {adminSessionWarning && (
        <Alert variant="warning" onClose={() => setAdminSessionWarning(null)} dismissible>
          <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
            <span>{adminSessionWarning}</span>
            <Button
              size="sm"
              variant="outline-dark"
              onClick={handleRenewBreakGlassSession}
              disabled={renewingAdminSession}
            >
              {renewingAdminSession ? "Renewing..." : "Renew Session"}
            </Button>
          </div>
        </Alert>
      )}

      <Tabs activeKey={tab} onSelect={(k) => setTab(k || "home")} className="mb-3">
        <Tab eventKey="home" title="OOB Admin Home">
          <Row className="g-3">
            <Col lg={4}>
              <Card>
                <Card.Header>Participants</Card.Header>
                <Card.Body>
                  <div className="h4 mb-2">{participants.length}</div>
                  <div className="text-muted small mb-3">Active and managed OOB participants.</div>
                  <Button variant="outline-primary" onClick={() => setTab("manageParticipants")}>Manage Participants</Button>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={4}>
              <Card>
                <Card.Header>Events</Card.Header>
                <Card.Body>
                  <div className="h4 mb-2">{events.length}</div>
                  <div className="text-muted small mb-3">Draft, active, and closed events.</div>
                  <Button variant="outline-primary" onClick={() => setTab("manageEvents")}>Manage Events</Button>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={4}>
              <Card>
                <Card.Header>Active Event</Card.Header>
                <Card.Body>
                  {activeEvent ? (
                    <>
                      <div className="fw-semibold mb-1">{activeEvent.eventName}</div>
                      <div className="small text-muted mb-2">{activeEvent.eventType}</div>
                      {activeEvent.isSilent && (
                        <Alert variant="warning" className="py-2 px-3 mb-3">
                          <strong>SILENT MODE ACTIVE</strong>
                          <div className="small">Creator-only notifications.</div>
                        </Alert>
                      )}
                      <div className="d-flex gap-2">
                        <Button variant="danger" onClick={() => { setSelectedEventGuid(activeEvent.eventGuid); setTab("activeEvent"); }}>
                          Open Active Event Control
                        </Button>
                        <Button
                          as="a"
                          href={publicEventPath(activeEvent.eventGuid)}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="outline-primary"
                        >
                          View Public Page
                        </Button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="text-muted small mb-3">No active event.</div>
                      <Button variant="outline-secondary" onClick={() => setTab("manageEvents")}>Create or Activate Event</Button>
                    </>
                  )}
                </Card.Body>
              </Card>
            </Col>
            <Col xs={12}>
              <Card>
                <Card.Header
                  className="d-flex justify-content-between align-items-center"
                  role="button"
                  tabIndex={0}
                  aria-expanded={showAdminGettingStarted}
                  style={{ cursor: "pointer" }}
                  onClick={() => setShowAdminGettingStarted((v) => !v)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setShowAdminGettingStarted((v) => !v);
                    }
                  }}
                >
                  <div className="d-flex align-items-center gap-2">
                    <span>Admin Getting Started</span>
                    <Badge bg={adminSetupIncomplete ? "warning" : "success"} text={adminSetupIncomplete ? "dark" : undefined}>
                      {adminSetupIncomplete ? "Admin setup needed" : "Admin ready"}
                    </Badge>
                  </div>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowAdminGettingStarted((v) => !v);
                    }}
                  >
                    {showAdminGettingStarted ? "Hide" : "Show"}
                  </Button>
                </Card.Header>
                <div
                  style={{
                    maxHeight: showAdminGettingStarted ? 420 : 0,
                    opacity: showAdminGettingStarted ? 1 : 0,
                    overflow: "hidden",
                    transition: "max-height 320ms ease, opacity 220ms ease",
                  }}
                >
                  <Card.Body>
                    <Row className="g-3">
                      <Col md={6} xl={3}>
                        <div className="fw-semibold mb-1">1. Confirm emergency access</div>
                        <div className="small text-muted mb-2">Set your emergency phone, then prove break-glass access works.</div>
                        <div className="d-flex flex-wrap gap-1 mb-2">
                          <Badge bg={adminEmergencyProfile?.isConfigured ? "success" : "warning"} text={adminEmergencyProfile?.isConfigured ? undefined : "dark"}>
                            {adminEmergencyProfile?.isConfigured ? "Profile configured" : "Profile needed"}
                          </Badge>
                          <Badge bg={adminEmergencyProfile?.hasSuccessfulBreakGlassTest ? "success" : "secondary"}>
                            {adminEmergencyProfile?.hasSuccessfulBreakGlassTest ? "Tested" : "Test not run"}
                          </Badge>
                        </div>
                        {adminEmergencyProfile?.lastSuccessfulBreakGlassTestDt && (
                          <div className="small text-muted mb-2">Last test: {fmt(adminEmergencyProfile.lastSuccessfulBreakGlassTestDt)}</div>
                        )}
                        <div className="d-flex flex-wrap gap-2">
                          <Button size="sm" variant="outline-primary" onClick={() => setShowAdminEmergencyModal(true)}>
                            Set Up Profile
                          </Button>
                          <Button size="sm" variant="outline-danger" href="/admin/oob/breakglass" target="_blank" rel="noopener noreferrer">
                            Test Break-Glass
                          </Button>
                        </div>
                      </Col>
                      <Col md={6} xl={3}>
                        <div className="d-flex align-items-center gap-2 mb-1">
                          <span className="fw-semibold">2. Review readiness</span>
                          <Badge bg={hasParticipants ? "success" : "secondary"}>{hasParticipants ? "Participants loaded" : "No participants"}</Badge>
                        </div>
                        <div className="small text-muted mb-2">Check personal email, phone verification, and secondary tenant identity status.</div>
                        <Button size="sm" variant="outline-primary" onClick={() => setTab("readinessDashboard")}>
                          Open Readiness
                        </Button>
                      </Col>
                      <Col md={6} xl={3}>
                        <div className="d-flex align-items-center gap-2 mb-1">
                          <span className="fw-semibold">3. Check files</span>
                          <Badge bg={hasArtifacts ? "success" : "secondary"}>{hasArtifacts ? "Files available" : "No files"}</Badge>
                        </div>
                        <div className="small text-muted mb-2">Upload or review runbooks, emergency plans, and checklist artifacts.</div>
                        <Button size="sm" variant="outline-primary" onClick={() => setTab("files")}>
                          Open Files
                        </Button>
                      </Col>
                      <Col md={6} xl={3}>
                        <div className="d-flex align-items-center gap-2 mb-1">
                          <span className="fw-semibold">4. Run a test event</span>
                          <Badge bg={hasEvents ? "success" : "secondary"}>{hasEvents ? "Events exist" : "No events"}</Badge>
                        </div>
                        <div className="small text-muted mb-2">Practice Draft → Activate → Resolve → Finalize → Deprovision before a live event.</div>
                        <Button size="sm" variant="outline-primary" onClick={() => setTab("manageEvents")}>
                          Create Test Event
                        </Button>
                      </Col>
                    </Row>
                  </Card.Body>
                </div>
              </Card>
            </Col>
          </Row>
        </Tab>

        <Tab eventKey="manageParticipants" title="Manage Participants">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div>Participant Groups ({groups.length})</div>
              <Button variant="primary" onClick={() => setShowCreateGroupModal(true)}>Add Group</Button>
            </Card.Header>
            <Card.Body>
              {groups.length === 0 ? (
                <div className="text-muted">No groups found. Create your first group to start organizing participants.</div>
              ) : (
                <div className="d-flex flex-column gap-3">
                  {groups.map((group) => {
                    const members = groupMembersByGroup[group.groupGuid] || [];
                    const expanded = expandedParticipantGroups.includes(group.groupGuid);

                    return (
                      <Card key={group.groupGuid}>
                        <Card.Header
                          className="d-flex justify-content-between align-items-center"
                          role="button"
                          tabIndex={0}
                          aria-expanded={expanded}
                          style={{ cursor: "pointer" }}
                          onClick={() => toggleParticipantGroupExpanded(group.groupGuid)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              toggleParticipantGroupExpanded(group.groupGuid);
                            }
                          }}
                        >
                          <div>
                            <div className="fw-semibold">{group.groupName}</div>
                            <div className="small text-muted">{group.memberCount} member(s)</div>
                          </div>
                          <div className="d-flex gap-2" onClick={(e) => e.stopPropagation()}>
                            <Button size="sm" variant="outline-primary" onClick={() => openAddMemberModal(group)}>
                              Add Member
                            </Button>
                            <Button size="sm" variant="outline-danger" onClick={() => openPurgeGroupModal(group)}>
                              Purge Group
                            </Button>
                            <Button size="sm" variant="outline-secondary" onClick={() => toggleParticipantGroupExpanded(group.groupGuid)}>
                              {expanded ? "Hide Members" : "Show Members"}
                            </Button>
                          </div>
                        </Card.Header>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateRows: expanded ? "1fr" : "0fr",
                            transition: "grid-template-rows 320ms ease, opacity 220ms ease",
                            opacity: expanded ? 1 : 0,
                          }}
                        >
                          <div style={{ overflow: "hidden" }}>
                            <Card.Body className="p-0">
                            <Table hover responsive className="mb-0">
                              <thead>
                                <tr>
                                  <th>Name</th>
                                  <th>Email</th>
                                  <th>Department</th>
                                  <th>Status</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {members.map((m) => (
                                  <tr key={`${group.groupGuid}-${m.participantGuid}`}>
                                    <td>{m.displayName}</td>
                                    <td>{m.email}</td>
                                    <td>{m.department || "-"}</td>
                                    <td><Badge bg={m.isActive ? "success" : "secondary"}>{m.isActive ? "Active" : "Inactive"}</Badge></td>
                                    <td>
                                      <Button size="sm" variant="outline-danger" onClick={() => handleRemoveParticipantFromGroup(group.groupGuid, m.participantGuid)}>Remove from Group</Button>
                                    </td>
                                  </tr>
                                ))}
                                {members.length === 0 && (
                                  <tr><td colSpan={5} className="text-center text-muted py-3">No members in this group.</td></tr>
                                )}
                              </tbody>
                            </Table>
                            </Card.Body>
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              )}
            </Card.Body>
          </Card>

          <Card className="mt-3">
            <Card.Header>Bulk Operations</Card.Header>
            <Card.Body>
              <Row className="g-2">
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Import Mode</Form.Label>
                    <Form.Select value={bulkMode} onChange={(e) => setBulkMode(e.target.value as OobBulkMode)}>
                      <option value="AddNew">Add New Members</option>
                      <option value="Merge">Merge Members</option>
                      <option value="ReplaceAll">Replace Group Members</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Target Group</Form.Label>
                    <Form.Select value={selectedBulkGroupGuid} onChange={(e) => setSelectedBulkGroupGuid(e.target.value)}>
                      <option value="">-- Select Group --</option>
                      {groups.map((group) => (
                        <option key={group.groupGuid} value={group.groupGuid}>
                          {group.groupName}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label className="d-flex align-items-center justify-content-between mb-1">
                      <span>CSV File</span>
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip id="oob-download-template-tooltip">Download template</Tooltip>}
                      >
                        <Button
                          variant="link"
                          className="p-0 text-decoration-none"
                          onClick={handleDownloadBulkTemplate}
                          aria-label="Download template"
                        >
                          <FaFileDownload />
                        </Button>
                      </OverlayTrigger>
                    </Form.Label>
                    <Form.Control type="file" accept=".csv" onChange={(e) => setBulkFile((e.target as HTMLInputElement).files?.[0] || null)} />
                  </Form.Group>
                </Col>
                <Col md={2} className="d-flex align-items-end gap-2">
                  <Button variant="success" className="text-white" onClick={handleBulkImport}>Import CSV</Button>
                  <Button variant="outline-danger" onClick={openPurgeAllModal}>Purge All</Button>
                </Col>
              </Row>
              {bulkMode === "ReplaceAll" && (
                <Alert variant="warning" className="mt-3 mb-0">
                  Replace Group Members will end-date all current memberships in the selected group, then refill from this CSV.
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="readinessDashboard" title="Readiness Dashboard">
          <Row className="g-3 mb-3">
            <Col lg={3}>
              <Card>
                <Card.Header>Overall Verification</Card.Header>
                <Card.Body>
                  <div className="h4 mb-1">{readinessDashboard?.totalFullyVerifiedPercent ?? 0}%</div>
                  <div className="small text-muted">
                    {readinessDashboard?.totalFullyVerified ?? 0} of {readinessDashboard?.totalParticipants ?? 0} fully verified
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={3}>
              <Card>
                <Card.Header>This Week</Card.Header>
                <Card.Body>
                  <div className="h5 mb-1">{readinessDashboard?.thisWeek.verifiedPercent ?? 0}%</div>
                  <div className="small text-muted">
                    {readinessDashboard?.thisWeek.verifiedCount ?? 0}/{readinessDashboard?.thisWeek.addedCount ?? 0} verified
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={3}>
              <Card>
                <Card.Header>This Month</Card.Header>
                <Card.Body>
                  <div className="h5 mb-1">{readinessDashboard?.thisMonth.verifiedPercent ?? 0}%</div>
                  <div className="small text-muted">
                    {readinessDashboard?.thisMonth.verifiedCount ?? 0}/{readinessDashboard?.thisMonth.addedCount ?? 0} verified
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={3}>
              <Card>
                <Card.Header>This Quarter</Card.Header>
                <Card.Body>
                  <div className="h5 mb-1">{readinessDashboard?.thisQuarter.verifiedPercent ?? 0}%</div>
                  <div className="small text-muted">
                    {readinessDashboard?.thisQuarter.verifiedCount ?? 0}/{readinessDashboard?.thisQuarter.addedCount ?? 0} verified
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center gap-2 flex-wrap">
              <div>Participant Verification Status</div>
              <div className="d-flex gap-2">
                <Form.Control
                  size="sm"
                  placeholder="Search participant, email, state"
                  value={readinessSearch}
                  onChange={(e) => setReadinessSearch(e.target.value)}
                  style={{ minWidth: 260 }}
                />
                <Button size="sm" variant="outline-secondary" onClick={refreshReadinessDashboard}>Refresh</Button>
              </div>
            </Card.Header>
            <Card.Body className="border-bottom">
              <Row className="g-2">
                <Col md={2}>
                  <Form.Select size="sm" value={readinessGroupFilter} onChange={(e) => setReadinessGroupFilter(e.target.value)}>
                    <option value="All">All Groups</option>
                    {groups.map((group) => (
                      <option key={group.groupGuid} value={group.groupGuid}>{group.groupName}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={2}>
                  <Form.Select size="sm" value={readinessStateFilter} onChange={(e) => setReadinessStateFilter(e.target.value)}>
                    <option value="All">All States</option>
                    {readinessStates.map((state) => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={2}>
                  <Form.Select size="sm" value={readinessVerificationFilter} onChange={(e) => setReadinessVerificationFilter(e.target.value)}>
                    <option value="All">All Verification</option>
                    <option value="FullyVerified">Fully Verified</option>
                    <option value="MissingEither">Missing Either</option>
                    <option value="MissingEmail">Missing Email</option>
                    <option value="MissingText">Missing Text</option>
                    <option value="OnboardingNotSent">Onboarding Not Sent</option>
                  </Form.Select>
                </Col>
                <Col md={2}>
                  <Form.Select size="sm" value={readinessPreferredFilter} onChange={(e) => setReadinessPreferredFilter(e.target.value)}>
                    <option value="All">All Preferred</option>
                    <option value="Email">Email</option>
                    <option value="Text">Text</option>
                  </Form.Select>
                </Col>
                <Col md={2}>
                  <Form.Select size="sm" value={readinessAddedWindowFilter} onChange={(e) => setReadinessAddedWindowFilter(e.target.value)}>
                    <option value="All">All Added Dates</option>
                    <option value="ThisWeek">Added This Week</option>
                    <option value="ThisMonth">Added This Month</option>
                    <option value="ThisQuarter">Added This Quarter</option>
                  </Form.Select>
                </Col>
                <Col md={2}>
                  <Form.Select size="sm" value={readinessInviteAgeFilter} onChange={(e) => setReadinessInviteAgeFilter(e.target.value)}>
                    <option value="All">All Invite Ages</option>
                    <option value="NotSent">Invite Not Sent</option>
                    <option value="LessThan1Day">Sent &lt; 1 Day</option>
                    <option value="Between1And7Days">Sent 1-7 Days</option>
                    <option value="Over7Days">Sent &gt; 7 Days</option>
                  </Form.Select>
                </Col>
                <Col md={2}>
                  <Form.Select size="sm" value={readinessActivityFilter} onChange={(e) => setReadinessActivityFilter(e.target.value)}>
                    <option value="All">All Activity</option>
                    <option value="Last7Days">Updated Last 7 Days</option>
                    <option value="Last30Days">Updated Last 30 Days</option>
                    <option value="Last90Days">Updated Last 90 Days</option>
                    <option value="OlderThan90Days">Updated &gt; 90 Days Ago</option>
                  </Form.Select>
                </Col>
                <Col md={2}>
                  <Form.Select size="sm" value={readinessGroupCountFilter} onChange={(e) => setReadinessGroupCountFilter(e.target.value)}>
                    <option value="All">All Group Counts</option>
                    <option value="NoGroup">No Group</option>
                    <option value="SingleGroup">Single Group</option>
                    <option value="MultiGroup">Multi Group</option>
                  </Form.Select>
                </Col>
              </Row>
              <Row className="g-2 mt-1 align-items-center">
                <Col md={3}>
                  <Form.Check
                    type="switch"
                    id="readiness-needs-action-switch"
                    label="Needs Action Only"
                    checked={readinessNeedsActionOnly}
                    onChange={(e) => setReadinessNeedsActionOnly(e.target.checked)}
                  />
                </Col>
                <Col md={3} className="small text-muted d-flex align-items-center">
                  Shows participants not in Ready state.
                </Col>
                <Col md={6} className="d-flex justify-content-end align-items-center">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    className="px-3"
                    onClick={() => {
                      setReadinessGroupFilter("All");
                      setReadinessStateFilter("All");
                      setReadinessVerificationFilter("All");
                      setReadinessPreferredFilter("All");
                      setReadinessAddedWindowFilter("All");
                      setReadinessInviteAgeFilter("All");
                      setReadinessActivityFilter("All");
                      setReadinessGroupCountFilter("All");
                      setReadinessNeedsActionOnly(false);
                      setReadinessSearch("");
                    }}
                  >
                    Clear Filters
                  </Button>
                </Col>
              </Row>
            </Card.Body>
            <Card.Body className="p-0">
              <Table hover responsive className="mb-0">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Added</th>
                    <th>Groups</th>
                    <th>State</th>
                    <th>Preferred</th>
                    <th>Personal Email</th>
                    <th>Email Verified</th>
                    <th>Text Verified</th>
                    <th>Secondary Identity</th>
                    <th>Onboarding Invite</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredReadinessParticipants.map((p) => (
                    <tr key={p.participantGuid}>
                      <td>{p.displayName}</td>
                      <td>{p.email}</td>
                      <td>{fmt(p.createDt)}</td>
                      <td>{p.groupCount}</td>
                      <td><Badge bg={p.readinessState === "Ready" ? "success" : "warning"}>{p.readinessState}</Badge></td>
                      <td>{p.preferredContactMethod}</td>
                      <td>{p.personalEmail || <span className="text-muted">Not set</span>}</td>
                      <td><Badge bg={p.personalEmailVerified ? "success" : "secondary"}>{p.personalEmailVerified ? "Yes" : "No"}</Badge></td>
                      <td><Badge bg={p.personalPhoneVerified ? "success" : "secondary"}>{p.personalPhoneVerified ? "Yes" : "No"}</Badge></td>
                      <td>
                        <Badge bg={p.secondaryIdentityStatus === "Ready" ? "success" : "secondary"}>
                          {p.secondaryIdentityStatus}
                        </Badge>
                        {!p.secondaryGuestObjectId && (
                          <Button
                            size="sm"
                            variant="outline-primary"
                            className="ms-2"
                            disabled={invitingParticipantGuid === p.participantGuid}
                            onClick={() => handleInviteSecondaryIdentity(p.participantGuid)}
                          >
                            {invitingParticipantGuid === p.participantGuid ? (
                              <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Starting...
                              </>
                            ) : (
                              "Invite"
                            )}
                          </Button>
                        )}
                      </td>
                      <td>{fmt(p.onboardingInviteSentDt)}</td>
                    </tr>
                  ))}
                  {filteredReadinessParticipants.length === 0 && (
                    <tr><td colSpan={11} className="text-center text-muted py-3">No participants match this filter.</td></tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="manageEvents" title="Manage Events">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div>
                Events ({filteredEvents.length})
                {filteredEvents.length !== events.length && <span className="text-muted"> / {events.length} total</span>}
              </div>
              <Button variant="primary" onClick={openCreateEventWizard}>Create Event</Button>
            </Card.Header>
            <Card.Body>
              <Row className="g-2 mb-3">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label className="mb-1">Filter by Type</Form.Label>
                    <Form.Select value={eventTypeFilter} onChange={(e) => setEventTypeFilter(e.target.value)}>
                      <option value="All">All Types</option>
                      {eventTypes.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label className="mb-1">Filter by Status</Form.Label>
                    <Form.Select value={eventStatusFilter} onChange={(e) => setEventStatusFilter(e.target.value)}>
                      <option value="All">All Statuses</option>
                      {eventStatuses.map((status) => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4} className="d-flex align-items-end">
                  <Button
                    variant="outline-secondary"
                    onClick={() => {
                      setEventTypeFilter("All");
                      setEventStatusFilter("All");
                    }}
                  >
                    Clear Filters
                  </Button>
                </Col>
              </Row>

              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead><tr><th>Name</th><th>Type</th><th>Communication</th><th>Status</th><th>Created</th><th>Actions</th></tr></thead>
                  <tbody>
                    {filteredEvents.map((evt) => (
                        <tr
                          key={evt.eventGuid}
                          role="button"
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            setSelectedEventGuid(evt.eventGuid);
                            setTab("activeEvent");
                          }}
                        >
                          <td>{evt.eventName}</td>
                          <td>{evt.eventType}</td>
                          <td>
                            <Badge
                              bg={evt.deliveryMode === "Local" || evt.deliveryStatus === "LocalActive" ? "info" : "primary"}
                              text={evt.deliveryMode === "Local" || evt.deliveryStatus === "LocalActive" ? "dark" : undefined}
                            >
                              {evt.deliveryMode === "Local" || evt.deliveryStatus === "LocalActive" ? "Local" : "Secondary tenant"}
                            </Badge>
                          </td>
                          <td>
                            <div className="d-flex gap-2 align-items-center">
                              <Badge bg={getEventStatusBadgeVariant(evt.status)}>{evt.status}</Badge>
                              {evt.isSilent && <Badge bg="danger" title="Notifications go only to the event creator">SILENT MODE</Badge>}
                            </div>
                          </td>
                          <td>{fmt(evt.createDt)}</td>
                          <td onClick={(e) => e.stopPropagation()}>
                            <div className="d-flex flex-wrap gap-1">
                              <OverlayTrigger placement="top" overlay={<Tooltip>Open event</Tooltip>}>
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  aria-label="Open event"
                                  onClick={() => {
                                    setSelectedEventGuid(evt.eventGuid);
                                    setTab("activeEvent");
                                  }}
                                >
                                  <FaCopy />
                                </Button>
                              </OverlayTrigger>
                              <OverlayTrigger placement="top" overlay={<Tooltip>Open public view</Tooltip>}>
                                <Button
                                  as="a"
                                  href={publicEventPath(evt.eventGuid)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  size="sm"
                                  variant="outline-secondary"
                                  aria-label="Open public view"
                                >
                                  <FaLink />
                                </Button>
                              </OverlayTrigger>
                              {evt.status === "Draft" && (
                                <OverlayTrigger placement="top" overlay={<Tooltip>Activate event</Tooltip>}>
                                  <Button
                                    size="sm"
                                    variant="danger"
                                    aria-label="Activate event"
                                    disabled={activatingEventGuid === evt.eventGuid}
                                    onClick={() => {
                                      setSelectedEventGuid(evt.eventGuid);
                                      setTimeout(() => {
                                        handleActivate();
                                      }, 0);
                                    }}
                                  >
                                    {activatingEventGuid === evt.eventGuid ? (
                                      <Spinner animation="border" size="sm" />
                                    ) : (
                                      <FaPlay />
                                    )}
                                  </Button>
                                </OverlayTrigger>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    {filteredEvents.length === 0 && (
                      <tr><td colSpan={6} className="text-center text-muted py-3">No events match the selected filters.</td></tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="platformAnnouncements" title="Platform Announcement">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div>
                Announcements ({filteredAnnouncements.length})
                {filteredAnnouncements.length !== announcementRows.length && <span className="text-muted"> / {announcementRows.length} total</span>}
              </div>
              <Button variant="primary" onClick={handleOpenAnnouncementModal}>Add Announcement</Button>
            </Card.Header>
            <Card.Body>
              <Row className="g-2 mb-3">
                <Col md={9}>
                  <Form.Group>
                    <Form.Label className="mb-1">Search</Form.Label>
                    <Form.Control
                      placeholder="Title, message, or creator"
                      value={announcementSearch}
                      onChange={(e) => setAnnouncementSearch(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={3} className="d-flex align-items-end">
                  <Button
                    variant="outline-secondary"
                    onClick={() => {
                      setAnnouncementSearch("");
                    }}
                  >
                    Clear Filters
                  </Button>
                </Col>
              </Row>

              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Message Preview</th>
                      <th>Created By</th>
                      <th>Recipients</th>
                      <th>Queued</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAnnouncements.map((row) => (
                      <tr key={row.announcementGuid}>
                        <td className="fw-semibold" style={{ maxWidth: "200px" }}>{row.title}</td>
                        <td className="small text-muted" style={{ maxWidth: "300px" }}>
                          {row.message.length > 100 ? `${row.message.substring(0, 100)}...` : row.message}
                        </td>
                        <td>{row.createdBy}</td>
                        <td>
                          <Badge bg="secondary">{row.recipientCount}</Badge>
                        </td>
                        <td>
                          <Badge bg={row.queuedCount > 0 ? "info" : "secondary"}>{row.queuedCount}</Badge>
                        </td>
                        <td className="small">{fmt(row.createDt)}</td>
                      </tr>
                    ))}
                    {filteredAnnouncements.length === 0 && (
                      <tr><td colSpan={6} className="text-center text-muted py-3">No platform announcements match the current filters.</td></tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="files" title="Files">
          <Tabs activeKey={filesSubTab} onSelect={(k) => setFilesSubTab(k || "masterGallery")} className="mt-3 mb-3">
            <Tab eventKey="masterGallery" title="Master Gallery">
              <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <span>Master Gallery ({artifactLibrary.length})</span>
                  <Button size="sm" variant="outline-secondary" onClick={handleRefreshArtifactLibrary}>Refresh</Button>
                </Card.Header>
                <Card.Body className="border-bottom">
                  <Row className="g-2 align-items-end">
                    <Col md={4}>
                      <Form.Label className="mb-1">Category</Form.Label>
                      <Form.Select
                        size="sm"
                        value={artifactUploadCategory}
                        onChange={(e) => setArtifactUploadCategory(e.target.value)}
                      >
                        <option value="RunBook">RunBook</option>
                        <option value="EmergencyPlan">EmergencyPlan</option>
                        <option value="Checklist">Checklist</option>
                        <option value="Other">Other</option>
                      </Form.Select>
                    </Col>
                    <Col md={5}>
                      <Form.Label className="mb-1">File</Form.Label>
                      <Form.Control
                        size="sm"
                        type="file"
                        key={artifactFileInputKey}
                        onChange={(e) => setArtifactUploadFile((e.target as HTMLInputElement).files?.[0] || null)}
                      />
                    </Col>
                    <Col md={3} className="d-grid">
                      <Button size="sm" onClick={handleUploadArtifact} disabled={!artifactUploadFile}>Upload</Button>
                    </Col>
                  </Row>
                </Card.Body>
                <Card.Body className="p-0">
                  <Table hover responsive className="mb-0">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Category</th>
                        <th>Size</th>
                        <th>Created</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {artifactLibrary.map((item) => (
                        <tr key={item.artifactGuid} className={!item.isActive ? "table-secondary" : undefined}>
                          <td>
                            <div className="fw-semibold">{item.artifactName}</div>
                            <div className="small text-muted">{item.relativePath}</div>
                          </td>
                          <td><ArtifactCategoryBadge category={item.artifactCategory} /></td>
                          <td>{fmtBytes(item.fileSizeBytes)}</td>
                          <td>{fmt(item.createDt)}</td>
                          <td>
                            <div className="d-flex gap-1 flex-wrap">
                              <Button
                                onClick={() => handleDownloadLibraryArtifact(item)}
                                size="sm"
                                variant="outline-secondary"
                              >
                                <FaFileDownload className="me-1" /> Download
                              </Button>
                              {item.isActive && (
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={() => handleArchiveArtifact(item.artifactGuid)}
                                >
                                  Archive
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                      {artifactLibrary.length === 0 && (
                        <tr><td colSpan={5} className="text-center text-muted py-3">No artifacts in the master gallery.</td></tr>
                      )}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Tab>

            <Tab eventKey="eventFiles" title="Event Files">
              <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <span>Event Files</span>
                  <Button size="sm" variant="outline-secondary" onClick={handleLoadEventArtifacts} disabled={!filesEventGuid}>
                    Refresh
                  </Button>
                </Card.Header>
                <Card.Body className="border-bottom">
                  <Row className="g-2 align-items-end">
                    <Col lg={8}>
                      <Form.Label className="mb-1">Event</Form.Label>
                      <Form.Select
                        size="sm"
                        value={filesEventGuid}
                        onChange={(e) => setFilesEventGuid(e.target.value)}
                      >
                        <option value="">-- Select Event --</option>
                        {fileEventGroups.recent.length > 0 && (
                          <optgroup label="Recent Events">
                            {fileEventGroups.recent.map((evt) => (
                              <option key={evt.eventGuid} value={evt.eventGuid}>
                                {evt.eventName} ({evt.status})
                              </option>
                            ))}
                          </optgroup>
                        )}
                        {fileEventGroups.olderClosed.length > 0 && (
                          <optgroup label="Archived Events">
                            {fileEventGroups.olderClosed.map((evt) => (
                              <option key={evt.eventGuid} value={evt.eventGuid}>
                                {evt.eventName} ({evt.status})
                              </option>
                            ))}
                          </optgroup>
                        )}
                      </Form.Select>
                    </Col>
                    <Col lg={4} className="small text-muted">
                      {filesEventGuid
                        ? (eventArtifactManifest ? `${eventArtifactManifest.artifactCount} in manifest` : "No manifest")
                        : "Select an event to view copied files."}
                    </Col>
                  </Row>
                  {eventArtifactManifest && (
                    <div className="small text-muted mt-2">
                      Manifest {eventArtifactManifest.manifestVersion} generated {fmt(eventArtifactManifest.generatedDt)} by {eventArtifactManifest.generatedBy}
                    </div>
                  )}
                </Card.Body>
                <Card.Body className="p-0">
                  <Table hover responsive className="mb-0">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Type</th>
                        <th>Status</th>
                        <th>Provisioned</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filesEventGuid && eventArtifacts.map((item) => (
                        <tr key={item.artifactGuid}>
                          <td>
                            <div className="fw-semibold">{item.artifactName}</div>
                            <div className="small text-muted">{item.storagePath || "-"}</div>
                          </td>
                          <td><ArtifactCategoryBadge category={item.artifactType} /></td>
                          <td>
                            <Badge bg={item.provisioningStatus === "Provisioned" ? "success" : "warning"}>
                              {item.provisioningStatus}
                            </Badge>
                          </td>
                          <td>{fmt(item.provisionedDt)}</td>
                          <td>
                            <Button size="sm" variant="outline-secondary" onClick={() => handleDownloadEventArtifact(item)}>
                              Download
                            </Button>
                          </td>
                        </tr>
                      ))}
                      {!filesEventGuid && (
                        <tr><td colSpan={5} className="text-center text-muted py-3">Select an event to view copied files.</td></tr>
                      )}
                      {filesEventGuid && eventArtifacts.length === 0 && (
                        <tr><td colSpan={5} className="text-center text-muted py-3">No files were copied for this event yet.</td></tr>
                      )}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Tab>
          </Tabs>
        </Tab>

        {selectedEvent && (
          <Tab eventKey="activeEvent" title={(
            <span className="oob-event-tab-title">
              <span>Event: {selectedEvent.eventName} ({selectedEvent.status})</span>
              <span
                className="oob-event-tab-close"
                role="button"
                tabIndex={0}
                aria-label="Close event tab"
                title="Close event tab"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  closeActiveEventTab();
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    event.stopPropagation();
                    closeActiveEventTab();
                  }
                }}
              >
                <FaTimes aria-hidden="true" />
              </span>
            </span>
          )}>
            {renderActiveEventPanel()}
          </Tab>
        )}
      </Tabs>

      {activeEvents.length > 0 && (
        <div className="small text-muted mt-2">
          Open events: {activeEvents.map((e) => e.eventName).join(", ")}
        </div>
      )}

      <Modal show={showAddEventParticipantModal} onHide={() => setShowAddEventParticipantModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add Participant to Event</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Select Participant</Form.Label>
            <Form.Select value={selectedParticipantToAdd} onChange={(event) => setSelectedParticipantToAdd(event.target.value)}>
              <option value="">-- Select --</option>
              {participants
                .filter((participant) => participant.isActive && !eventParticipants.some((eventParticipant) => eventParticipant.participantGuid === participant.participantGuid))
                .map((participant) => (
                  <option key={participant.participantGuid} value={participant.participantGuid}>
                    {participant.displayName} ({participant.email})
                  </option>
                ))}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddEventParticipantModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleAddParticipantToEvent} disabled={loading || !selectedParticipantToAdd}>Add Participant</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showPostUpdateModal} onHide={() => setShowPostUpdateModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Post Event Update</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handlePostUpdate}>
          <Modal.Body>
            <Form.Group className="mb-3"><Form.Label>Title</Form.Label><Form.Control value={updateForm.messageTitle} onChange={(event) => setUpdateForm((current) => ({ ...current, messageTitle: event.target.value }))} required /></Form.Group>
            <Form.Group className="mb-3"><Form.Label>Message</Form.Label><Form.Control as="textarea" rows={5} value={updateForm.messageBody} onChange={(event) => setUpdateForm((current) => ({ ...current, messageBody: event.target.value }))} required /></Form.Group>
            <Form.Group className="mb-3"><Form.Label>Importance</Form.Label><Form.Select value={updateForm.importance} onChange={(event) => setUpdateForm((current) => ({ ...current, importance: event.target.value as OobCreateUpdateRequest["importance"] }))}><option value="Normal">Normal</option><option value="Important">Important</option><option value="Critical">Critical</option></Form.Select></Form.Group>
            <Form.Check label="Pin as primary instruction" checked={updateForm.isPinned} onChange={(event) => setUpdateForm((current) => ({ ...current, isPinned: event.target.checked }))} />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowPostUpdateModal(false)} disabled={loading}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={loading || !updateForm.messageTitle.trim() || !updateForm.messageBody.trim()}>{loading ? "Posting..." : "Post Update"}</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={!!editEventQuestion} onHide={() => setEditEventQuestion(null)} centered size="lg">
        <Modal.Header closeButton><Modal.Title>Edit Event Question</Modal.Title></Modal.Header>
        {editEventQuestion && <>
          <Modal.Body>
            <Form.Group className="mb-3"><Form.Label>Question</Form.Label><Form.Control as="textarea" rows={3} maxLength={2000} value={editEventQuestion.questionText} onChange={(event) => setEditEventQuestion((current) => current ? { ...current, questionText: event.target.value } : current)} /></Form.Group>
            <Form.Group className="mb-3"><Form.Label>Answer</Form.Label><Form.Control as="textarea" rows={4} maxLength={4000} value={editEventQuestion.answerText || ""} onChange={(event) => setEditEventQuestion((current) => current ? { ...current, answerText: event.target.value } : current)} /></Form.Group>
            <Form.Group className="mb-3"><Form.Label>Importance</Form.Label><Form.Select value={editEventQuestion.importance} onChange={(event) => setEditEventQuestion((current) => current ? { ...current, importance: event.target.value as OobEditEventQuestionRequest["importance"] } : current)}><option value="Normal">Normal</option><option value="Important">Important</option><option value="Critical">Critical</option></Form.Select></Form.Group>
            <Form.Check type="switch" id="edit-event-question-anonymous" label="Display anonymously if published" checked={editEventQuestion.displayAsAnonymous} onChange={(event) => setEditEventQuestion((current) => current ? { ...current, displayAsAnonymous: event.target.checked } : current)} />
          </Modal.Body>
          <Modal.Footer><Button variant="secondary" onClick={() => setEditEventQuestion(null)} disabled={loading}>Cancel</Button><Button variant="primary" onClick={handleSaveEventQuestion} disabled={loading || !editEventQuestion.questionText.trim()}>{loading ? "Saving..." : "Save Changes"}</Button></Modal.Footer>
        </>}
      </Modal>

      <Modal
        show={showLocalPivotModal}
        onHide={() => setShowLocalPivotModal(false)}
        backdrop="static"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Activate Local Communication Space</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="danger">
            <strong>Teams provisioning did not complete.</strong>
            <div className="small mt-1">
              {selectedEvent?.deliveryError || secondaryProvisioningStatus?.error || "Review the event details before continuing."}
            </div>
          </Alert>
          <Form>
            <Form.Group className="mb-2">
              <Form.Label>Event Name</Form.Label>
              <Form.Control value={pivotEventForm.eventName} onChange={(event) => setPivotEventForm((current) => ({ ...current, eventName: event.target.value }))} required />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Event Type</Form.Label>
              <Form.Select value={pivotEventForm.eventType} onChange={(event) => setPivotEventForm((current) => ({ ...current, eventType: event.target.value as OobCreateEventRequest["eventType"] }))}>
                <option value="Test">Test</option>
                <option value="Onboarding">Onboarding</option>
                <option value="Activation">Activation</option>
                <option value="AnnualReview">Annual Review</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Internal Verification Note</Form.Label>
              <Form.Control value={pivotEventForm.safetyPhrase} onChange={(event) => setPivotEventForm((current) => ({ ...current, safetyPhrase: event.target.value }))} required />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Participant Description</Form.Label>
              <Form.Control as="textarea" rows={2} value={pivotEventForm.description} onChange={(event) => setPivotEventForm((current) => ({ ...current, description: event.target.value }))} />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Target Groups</Form.Label>
              <div className="border rounded p-2" style={{ maxHeight: 150, overflowY: "auto" }}>
                {groups.map((group) => (
                  <Form.Check
                    key={`pivot-group-${group.groupGuid}`}
                    id={`pivot-group-${group.groupGuid}`}
                    label={`${group.groupName} (${group.memberCount})`}
                    checked={pivotEventForm.groupGuids.includes(group.groupGuid)}
                    onChange={() => setPivotEventForm((current) => ({
                      ...current,
                      groupGuids: current.groupGuids.includes(group.groupGuid)
                        ? current.groupGuids.filter((groupGuid) => groupGuid !== group.groupGuid)
                        : [...current.groupGuids, group.groupGuid],
                    }))}
                  />
                ))}
              </div>
            </Form.Group>
            <Form.Check type="switch" id="pivot-silent-mode" label="Silent Mode (send only to event creator)" checked={pivotEventForm.isSilent} onChange={(event) => setPivotEventForm((current) => ({ ...current, isSilent: event.target.checked }))} />
          </Form>
          <div className="small text-muted mt-3">
            Saving records the revised event and replaces its pre-activation group audience. Local activation then queues personal-email invitations for this audience.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowLocalPivotModal(false)} disabled={loading}>Cancel</Button>
          <Button variant="outline-primary" onClick={handleSavePivotReview} disabled={loading || savingPivotReview || !pivotEventForm.eventName.trim() || !pivotEventForm.safetyPhrase.trim()}>
            {savingPivotReview ? "Saving..." : "Save Review"}
          </Button>
          <Button variant="primary" onClick={handleActivateLocalEventSpace} disabled={loading || savingPivotReview}>
            {loading ? "Activating..." : "Activate Local Space"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showPurgeAllModal} onHide={() => setShowPurgeAllModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Purge Options</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="small text-muted mb-3">
            Choose what should be purged. At least one option is required.
          </div>
          <Form.Check
            className="mb-2"
            type="checkbox"
            id="purge-all-participants"
            label="Purge all participants"
            checked={purgeAllOptions.purgeParticipants}
            onChange={(e) => setPurgeAllOptions((prev) => ({ ...prev, purgeParticipants: e.target.checked }))}
          />
          <Form.Check
            type="checkbox"
            id="purge-all-groups"
            label="Purge all groups and memberships"
            checked={purgeAllOptions.purgeGroups}
            onChange={(e) => setPurgeAllOptions((prev) => ({ ...prev, purgeGroups: e.target.checked }))}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPurgeAllModal(false)}>Cancel</Button>
          <Button
            variant="danger"
            onClick={handlePurgeAll}
            disabled={!purgeAllOptions.purgeParticipants && !purgeAllOptions.purgeGroups}
          >
            Run Purge
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showPurgeGroupModal} onHide={() => setShowPurgeGroupModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Purge Group</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            Purge group <strong>{selectedGroupForPurge?.groupName || ""}</strong>?
          </div>
          <Form.Check
            type="checkbox"
            id="purge-group-participants"
            label="Purge all participants currently assigned to this group"
            checked={purgeGroupParticipants}
            onChange={(e) => setPurgeGroupParticipants(e.target.checked)}
          />
          <Form.Check
            className="mt-2"
            type="checkbox"
            id="archive-group-after-purge"
            label="Delete (archive) group after participants are purged"
            checked={archiveGroupAfterPurge}
            onChange={(e) => setArchiveGroupAfterPurge(e.target.checked)}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPurgeGroupModal(false)}>Cancel</Button>
          <Button variant="danger" onClick={handlePurgeGroup}>Purge Group</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!confirmAction} onHide={() => setConfirmAction(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{confirmModal.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{confirmModal.body}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setConfirmAction(null)}>Cancel</Button>
          <Button variant={confirmModal.variant} onClick={handleConfirmAction}>{confirmModal.confirmLabel}</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!editUpdateState} onHide={() => setEditUpdateState(null)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Update</Modal.Title>
        </Modal.Header>
        {editUpdateState && (
          <>
            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>Title</Form.Label>
                <Form.Control
                  value={editUpdateState.messageTitle}
                  onChange={(e) => setEditUpdateState((prev) => prev ? { ...prev, messageTitle: e.target.value } : prev)}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Message</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={editUpdateState.messageBody}
                  onChange={(e) => setEditUpdateState((prev) => prev ? { ...prev, messageBody: e.target.value } : prev)}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Importance</Form.Label>
                <Form.Select
                  value={editUpdateState.importance}
                  onChange={(e) => setEditUpdateState((prev) => prev ? { ...prev, importance: e.target.value as "Normal" | "Important" | "Critical" } : prev)}
                >
                  <option value="Normal">Normal</option>
                  <option value="Important">Important</option>
                  <option value="Critical">Critical</option>
                </Form.Select>
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setEditUpdateState(null)}>Cancel</Button>
              <Button variant="primary" onClick={handleSaveEditedUpdate}>Save</Button>
            </Modal.Footer>
          </>
        )}
      </Modal>

      <Modal show={showCreateEventWizard} onHide={closeCreateEventWizard} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Create Event Wizard</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3 d-flex gap-2">
            <Badge bg={createEventStep === 1 ? "primary" : "secondary"}>1. Event Details</Badge>
            <Badge bg={createEventStep === 2 ? "primary" : "secondary"}>2. Review & Create</Badge>
          </div>

          {createEventStep === 1 && (
            <Form>
              <Form.Group className="mb-2">
                <Form.Label>Event Name</Form.Label>
                <Form.Control
                  value={eventForm.eventName}
                  onChange={(e) => setEventForm((v) => ({ ...v, eventName: e.target.value }))}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Type</Form.Label>
                <Form.Select value={eventForm.eventType} onChange={(e) => setEventForm((v) => ({ ...v, eventType: e.target.value as any }))}>
                  <option value="Test">Test</option>
                  <option value="Onboarding">Onboarding</option>
                  <option value="Activation">Activation</option>
                  <option value="AnnualReview">Annual Review</option>
                </Form.Select>
                {eventForm.eventType === "Onboarding" && (
                  <div className="small text-info mt-1">
                    Onboarding events confirm a participant's backup identity and basic OOB access before a live event.
                  </div>
                )}
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Internal Verification Note</Form.Label>
                <Form.Control
                  value={eventForm.safetyPhrase}
                  onChange={(e) => setEventForm((v) => ({ ...v, safetyPhrase: e.target.value }))}
                  required
                />
                <div className="small text-muted mt-1">Internal only. Not shown on participant event pages.</div>
              </Form.Group>
              <Form.Group className="mb-0">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={eventForm.description}
                  onChange={(e) => setEventForm((v) => ({ ...v, description: e.target.value }))}
                />
              </Form.Group>
              <Form.Group className="mt-3 mb-0">
                <Form.Check
                  type="switch"
                  id="event-silent-mode"
                  label="Silent Mode (send only to event creator)"
                  checked={eventForm.isSilent}
                  onChange={(e) => setEventForm((prev) => ({ ...prev, isSilent: e.target.checked }))}
                />
                {eventForm.isSilent && (
                  <Alert variant="warning" className="mt-2 mb-0 py-2 px-3">
                    <strong>SILENT MODE</strong>
                    <div className="small">This event will not notify participants. Only the creator receives notification copies.</div>
                  </Alert>
                )}
                <div className="small text-muted mt-1">
                  When enabled, notifications are queued only for the event creator and include a silent-mode banner.
                </div>
              </Form.Group>
              <Form.Group className="mt-3 mb-0">
                <Form.Check
                  type="switch"
                  id="event-create-local"
                  label="Create as a local event (skip Teams provisioning)"
                  checked={eventForm.createLocalEvent}
                  onChange={(e) => setEventForm((prev) => ({ ...prev, createLocalEvent: e.target.checked }))}
                />
                {eventForm.createLocalEvent && (
                  <div className="small text-muted mt-1">
                    The local communication space will activate immediately, with channels and participant email invitations queued.
                  </div>
                )}
              </Form.Group>
              <Form.Group className="mt-3 mb-0">
                <Form.Label>Target Groups</Form.Label>
                <div className="border rounded p-2" style={{ maxHeight: 200, overflowY: "auto" }}>
                  {groups.length === 0 ? (
                    <div className="small text-muted">No participant groups found.</div>
                  ) : (
                    groups.map((group) => (
                      <Form.Check
                        key={`event-group-${group.groupGuid}`}
                        type="checkbox"
                        id={`event-group-${group.groupGuid}`}
                        checked={eventForm.groupGuids.includes(group.groupGuid)}
                        onChange={() =>
                          setEventForm((prev) => ({
                            ...prev,
                            groupGuids: prev.groupGuids.includes(group.groupGuid)
                              ? prev.groupGuids.filter((g) => g !== group.groupGuid)
                              : [...prev.groupGuids, group.groupGuid],
                          }))
                        }
                        label={`${group.groupName} (${group.memberCount})`}
                        className="mb-1"
                      />
                    ))
                  )}
                </div>
                <div className="small text-muted mt-1">
                  Selected groups define the intended audience. In silent mode, each intended message is delivered to the event creator instead.
                </div>
              </Form.Group>
            </Form>
          )}

          {createEventStep === 2 && (
            <Card>
              <Card.Body>
                <div className="mb-2"><strong>Name:</strong> {eventForm.eventName || "-"}</div>
                <div className="mb-2"><strong>Type:</strong> {eventForm.eventType || "-"}</div>
                <div className="mb-2"><strong>Event space:</strong> {eventForm.createLocalEvent ? "Local (activate immediately; skip Teams provisioning)" : "Teams-backed (create draft)"}</div>
                <div className="mb-2"><strong>Silent Mode:</strong> {eventForm.isSilent ? "Yes (creator only)" : "No"}</div>
                <div className="mb-2"><strong>Internal Verification Note:</strong> {eventForm.safetyPhrase || "-"}</div>
                <div className="mb-2"><strong>Description:</strong> {eventForm.description || "-"}</div>
                <div className="mb-0"><strong>Groups:</strong> {eventForm.groupGuids.length > 0 ? `${eventForm.groupGuids.length} selected` : "None selected"}</div>
              </Card.Body>
            </Card>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeCreateEventWizard}>Cancel</Button>
          {createEventStep === 2 && (
            <Button variant="outline-secondary" onClick={() => setCreateEventStep(1)}>Back</Button>
          )}
          {createEventStep === 1 && (
            <Button
              variant="primary"
              onClick={() => setCreateEventStep(2)}
              disabled={!eventForm.eventName.trim() || !eventForm.safetyPhrase.trim()}
            >
              Next
            </Button>
          )}
          {createEventStep === 2 && (
            <Button variant="primary" onClick={handleCreateEvent} disabled={loading}>
              {loading ? "Creating..." : "Create Event"}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      <Modal show={showAdminEmergencyModal} onHide={() => setShowAdminEmergencyModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Emergency Access Profile</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveAdminEmergencyProfile}>
          <Modal.Body>
            <Alert variant="info" className="py-2">
              This profile enables break-glass admin access when normal Microsoft sign-in is unavailable.
            </Alert>
            <Form.Group className="mb-2">
              <Form.Label>Admin Email</Form.Label>
              <Form.Control value={adminEmergencyProfile?.email || ""} disabled />
              <Form.Text className="text-muted">
                Used only to identify your admin record. Break-glass codes are sent to your emergency email.
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Emergency Email</Form.Label>
              <Form.Control
                type="email"
                value={adminEmergencyEmail}
                onChange={(e) => setAdminEmergencyEmail(e.target.value)}
                placeholder="you@example.com"
                disabled={loading}
                required
              />
              <Form.Text className="text-muted">Use a non-ENMAX address so codes still arrive if corporate mail is unavailable.</Form.Text>
            </Form.Group>
            <Form.Group className="mb-0">
              <Form.Label>Emergency Phone</Form.Label>
              <Form.Control
                value={adminEmergencyPhone}
                onChange={(e) => setAdminEmergencyPhone(e.target.value)}
                placeholder="e.g. +1 555 0100"
                disabled={loading}
                required
              />
              <Form.Text className="text-muted">
                For this demo, break-glass SMS is simulated by email, but this number records the intended emergency contact method.
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAdminEmergencyModal(false)} disabled={loading}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={loading || !adminEmergencyEmail.trim() || !adminEmergencyPhone.trim()}>
              {loading ? "Saving..." : "Save Profile"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showCreateGroupModal} onHide={() => setShowCreateGroupModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add Participant Group</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreateGroup}>
          <Modal.Body>
            <Form.Group className="mb-2">
              <Form.Label>Group Name</Form.Label>
              <Form.Control
                value={groupForm.groupName}
                onChange={(e) => setGroupForm((v) => ({ ...v, groupName: e.target.value }))}
                required
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Sort Order</Form.Label>
              <Form.Control
                type="number"
                value={groupForm.sortOrder}
                onChange={(e) => setGroupForm((v) => ({ ...v, sortOrder: Number(e.target.value) || 0 }))}
              />
            </Form.Group>
            <Form.Group className="mb-0">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={groupForm.description}
                onChange={(e) => setGroupForm((v) => ({ ...v, description: e.target.value }))}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCreateGroupModal(false)}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={loading}>Create Group</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showAddMemberModal} onHide={() => setShowAddMemberModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add Member{targetGroupForNewMember ? ` to ${targetGroupForNewMember.groupName}` : ""}</Modal.Title>
        </Modal.Header>
        <Tabs activeKey={addMemberMode} onSelect={(key) => setAddMemberMode((key as "existing" | "new") || "existing")} className="px-3 pt-2">
          <Tab eventKey="existing" title="Add existing">
            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>Participant</Form.Label>
                <div className="position-relative">
                  <div className="input-group">
                    <Form.Control
                      type="search"
                      role="combobox"
                      aria-autocomplete="list"
                      aria-expanded={isExistingParticipantMenuOpen}
                      aria-controls="oob-existing-participant-options"
                      value={existingParticipantSearch}
                      onFocus={() => setIsExistingParticipantMenuOpen(true)}
                      onChange={(event) => {
                        setExistingParticipantSearch(event.target.value);
                        setSelectedExistingParticipantGuid("");
                        setIsExistingParticipantMenuOpen(true);
                      }}
                      onBlur={() => window.setTimeout(() => setIsExistingParticipantMenuOpen(false), 120)}
                      onKeyDown={(event) => {
                        if (event.key === "Escape") setIsExistingParticipantMenuOpen(false);
                        if (event.key === "ArrowDown") setIsExistingParticipantMenuOpen(true);
                      }}
                      placeholder="Select or type a name or email to filter"
                      autoComplete="off"
                      disabled={loading}
                    />
                    <Button
                      type="button"
                      variant="outline-secondary"
                      aria-label={isExistingParticipantMenuOpen ? "Close participant options" : "Show participant options"}
                      aria-expanded={isExistingParticipantMenuOpen}
                      disabled={loading}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => {
                        const open = !isExistingParticipantMenuOpen;
                        setIsExistingParticipantMenuOpen(open);
                        if (open) {
                          setExistingParticipantSearch("");
                          setSelectedExistingParticipantGuid("");
                        }
                      }}
                    >
                      <FaAngleRight style={{ transform: isExistingParticipantMenuOpen ? "rotate(90deg)" : undefined, transition: "transform 120ms ease" }} aria-hidden="true" />
                    </Button>
                  </div>
                  {isExistingParticipantMenuOpen && (
                    <div id="oob-existing-participant-options" role="listbox" className="list-group position-absolute w-100 shadow-sm" style={{ zIndex: 1056, maxHeight: 240, overflowY: "auto" }}>
                      {filteredAvailableParticipants.length === 0 ? (
                        <div className="list-group-item small text-muted">No active participants available for this group.</div>
                      ) : filteredAvailableParticipants.map((participant) => (
                        <button
                          key={participant.participantGuid}
                          type="button"
                          role="option"
                          aria-selected={selectedExistingParticipantGuid === participant.participantGuid}
                          className={`list-group-item list-group-item-action ${selectedExistingParticipantGuid === participant.participantGuid ? "active" : ""}`}
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => {
                            setSelectedExistingParticipantGuid(participant.participantGuid);
                            setExistingParticipantSearch(`${participant.displayName} · ${participant.email}`);
                            setIsExistingParticipantMenuOpen(false);
                          }}
                        >
                          <span className="d-block fw-semibold">{participant.displayName}</span>
                          <span className="d-block small">{participant.email}{participant.department ? ` · ${participant.department}` : ""}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <Form.Text className="text-muted">Click to browse active participants or type to filter by name or email.</Form.Text>
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowAddMemberModal(false)}>Cancel</Button>
              <Button type="button" variant="primary" disabled={loading || !selectedExistingParticipantGuid} onClick={() => void handleAddSelectedExistingParticipant()}>Add to Group</Button>
            </Modal.Footer>
          </Tab>
          <Tab eventKey="new" title="Add new">
            <Form onSubmit={handleCreateParticipantInGroup}>
              <Modal.Body>
                <Form.Group className="mb-3">
                  <Form.Label>Find employee by name or email</Form.Label>
                  <Form.Control
                    type="search"
                    value={employeeSearch}
                    onChange={(event) => { setEmployeeSearch(event.target.value); setSelectedDirectoryEmployee(null); }}
                    placeholder="Start typing a first name, last name, or email"
                    autoComplete="off"
                    disabled={loading}
                  />
                  <Form.Text className="text-muted">Enter at least two characters, then select an employee to prefill the form.</Form.Text>
                </Form.Group>
                {employeeSearchLoading && <div className="small text-muted mb-2"><Spinner animation="border" size="sm" className="me-2" />Searching employee directory...</div>}
                {!employeeSearchLoading && !selectedDirectoryEmployee && employeeSearch.trim().length >= 2 && employeeSearchResults.length === 0 && <div className="small text-muted mb-2">No active employees matched that search.</div>}
                {!selectedDirectoryEmployee && employeeSearchResults.length > 0 && <div className="d-flex flex-column gap-2 mb-3" role="listbox" aria-label="Employee directory matches">
                  {employeeSearchResults.map((employee) => {
                    const existing = participants.find((person) => person.isActive && person.email.trim().toLowerCase() === employee.email.trim().toLowerCase());
                    const alreadyInTargetGroup = Boolean(existing?.groupGuids.includes(targetGroupForNewMember?.groupGuid || ""));
                    return <Button key={`${employee.employeeId}-${employee.email}`} type="button" variant="outline-secondary" className="text-start" disabled={loading || alreadyInTargetGroup} onClick={() => handleSelectDirectoryEmployee(employee)}>
                      <span className="d-block fw-semibold">{employee.displayName}</span>
                      <span className="d-block small">{employee.email}{employee.department ? ` · ${employee.department}` : ""}{employee.jobTitle ? ` · ${employee.jobTitle}` : ""}</span>
                      {alreadyInTargetGroup && <span className="d-block small text-muted">Already in this group</span>}
                    </Button>;
                  })}
                </div>}
                {selectedDirectoryEmployee && matchingActiveParticipantsForNewEmail.length === 1 && <Alert variant={activeMemberGuidsInTargetGroup.has(matchingActiveParticipantsForNewEmail[0].participantGuid) ? "info" : "warning"} className="mb-3 py-2">
                  {activeMemberGuidsInTargetGroup.has(matchingActiveParticipantsForNewEmail[0].participantGuid)
                    ? "This employee already has an OOB participant record and is already in this group."
                    : <>This employee already has an OOB participant record. <Button type="button" variant="link" className="p-0 ms-1" disabled={loading} onClick={() => void handleAddKnownParticipantToTargetGroup(matchingActiveParticipantsForNewEmail[0])}>Add that existing record to this group</Button></>}
                </Alert>}
                {selectedDirectoryEmployee && matchingActiveParticipantsForNewEmail.length > 1 && <Alert variant="danger" className="mb-3 py-2">Multiple active OOB records already use this email. Resolve those duplicates before adding the employee.</Alert>}
                <Form.Group className="mb-2">
                  <Form.Label>Display Name</Form.Label>
                  <Form.Control value={participantForm.displayName} onChange={(event) => setParticipantForm((current) => ({ ...current, displayName: event.target.value }))} required disabled={!selectedDirectoryEmployee} />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>Email</Form.Label>
                  <Form.Control type="email" value={participantForm.email} readOnly required />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>Employee ID</Form.Label>
                  <Form.Control value={participantForm.employeeId} readOnly required />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>Department</Form.Label>
                  <Form.Control value={participantForm.department} onChange={(event) => setParticipantForm((current) => ({ ...current, department: event.target.value }))} disabled={!selectedDirectoryEmployee} />
                </Form.Group>
                <Form.Group className="mb-0">
                  <Form.Label>Phone</Form.Label>
                  <Form.Control value={participantForm.phoneNumber} onChange={(event) => setParticipantForm((current) => ({ ...current, phoneNumber: event.target.value }))} disabled={!selectedDirectoryEmployee} />
                </Form.Group>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowAddMemberModal(false)}>Cancel</Button>
                <Button type="submit" variant="primary" disabled={loading || !targetGroupForNewMember || !selectedDirectoryEmployee || matchingActiveParticipantsForNewEmail.length > 0}>Create and Add</Button>
              </Modal.Footer>
            </Form>
          </Tab>
        </Tabs>
      </Modal>

      <Modal show={showAnnouncementModal} onHide={() => setShowAnnouncementModal(false)} centered size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Add Platform Announcement</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col lg={5}>
              <Card>
                <Card.Header>Announcement Details</Card.Header>
                <Card.Body>
                  <div className="small text-muted mb-3">
                    Queue this message for review and publish via the Email Approval flow.
                  </div>
                  <Form onSubmit={handleQueuePlatformAnnouncement}>
                    <Form.Group className="mb-2">
                      <Form.Label>Title</Form.Label>
                      <Form.Control
                        value={announcementForm.title}
                        onChange={(e) => setAnnouncementForm((v) => ({ ...v, title: e.target.value }))}
                        required
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Message</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={6}
                        value={announcementForm.message}
                        onChange={(e) => setAnnouncementForm((v) => ({ ...v, message: e.target.value }))}
                        required
                      />
                    </Form.Group>

                    <div className="small text-muted mb-3">
                      Targeting summary: {selectedAnnouncementGroups.length} group(s), {includedAnnouncementParticipants.length} explicit include(s), {excludedAnnouncementParticipants.length} explicit exclude(s)
                    </div>

                    <Button type="submit" variant="primary" disabled={loading}>Queue Announcement</Button>
                  </Form>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={7}>
              <Card>
                <Card.Header>Groups ({groups.length})</Card.Header>
                <Card.Body>
                  {groups.length === 0 ? (
                    <div className="text-muted">No groups configured yet.</div>
                  ) : (
                    <div className="d-flex flex-column gap-2">
                      {groups.map((group) => {
                        const members = groupMembersByGroup[group.groupGuid] || [];
                        const expanded = expandedGroups.includes(group.groupGuid);
                        const selected = isGroupSelected(group.groupGuid);

                        return (
                          <Card key={group.groupGuid}>
                            <Card.Body>
                              <div className="d-flex justify-content-between align-items-start gap-2">
                                <div>
                                  <Form.Check
                                    type="checkbox"
                                    id={`announcement-group-${group.groupGuid}`}
                                    label={`${group.groupName} (${group.memberCount})`}
                                    checked={selected}
                                    onChange={() => toggleGroupSelected(group.groupGuid)}
                                  />
                                  {group.description && <div className="small text-muted ms-4">{group.description}</div>}
                                </div>
                                <Button size="sm" variant="outline-secondary" onClick={() => toggleGroupExpanded(group.groupGuid)}>
                                  {expanded ? "Hide Members" : "Show Members"}
                                </Button>
                              </div>

                              {expanded && (
                                <div className="mt-3 border rounded p-2" style={{ maxHeight: 240, overflowY: "auto" }}>
                                  {members.length === 0 ? (
                                    <div className="small text-muted">No members found for this group.</div>
                                  ) : (
                                    members.map((m) => (
                                      <Form.Check
                                        key={`announcement-member-${group.groupGuid}-${m.participantGuid}`}
                                        type="checkbox"
                                        id={`announcement-member-${group.groupGuid}-${m.participantGuid}`}
                                        checked={isMemberSelected(group.groupGuid, m.participantGuid)}
                                        onChange={() => toggleMemberSelected(group.groupGuid, m.participantGuid)}
                                        label={`${m.displayName} (${m.email})`}
                                        className="mb-1"
                                      />
                                    ))
                                  )}
                                </div>
                              )}
                            </Card.Body>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default AdminOutOfBandBoard;
